// GuideBuilder.jsx - Main guide building interface (refactored modular version)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAccessControl } from '../contexts/AccessControlContext';
import { useGuides } from '../contexts/GuidesContext';
import { useLanguage } from './ItineraryCreator/LanguageContext';

// Import custom hooks
import { useItineraryState } from './hooks/useItineraryState';
import { useUIState } from './hooks/useUIState';
import { useDraftState } from './hooks/useDraftState';
import { useTranslationState } from './hooks/useTranslationState';
import { useAudioGeneration } from './hooks/useAudioGeneration';
import { useFieldGeneration } from './hooks/useFieldGeneration';
import { useImageUpload } from './hooks/useImageUpload';
import { useItineraryContent } from './hooks/useItineraryContent';
import { useTourGeneration } from './hooks/useTourGeneration';
import { useAiModel } from './hooks/useAiModel';
import { usePromptTemplates } from './promptTemplates/index_prompt_templates';

// Import services
import { ItineraryActions } from './services/ItineraryActions';
import { FileOperations } from './services/FileOperations';
import { DatabaseOperations } from './services/DatabaseOperations';
import { aiService } from './services/aiService';
import { qaService } from './services/qaService';
import { contentRewriteService } from './services/contentRewriteService';
import { prepareItineraryForPublication } from './services/automaticGuideResearch';

// Import layout components
import ItineraryLayout from './layout/ItineraryLayout';
import BuilderTabs from './layout/BuilderTabs';
import StepWizard from './layout/StepWizard';
import ActionButtons from './layout/ActionButtons';
import SeoPageButton from './layout/SeoPageButton';
import { GenerateAllSeoButton } from './layout/GenerateAllSeoButton';
import { GenerateIndexOnlyButton } from './layout/GenerateIndexOnlyButton';

// Import dialog components
import SqlDialog from './dialogs/SqlDialog';
import LoadGuideDialog from './dialogs/LoadGuideDialog';
import PermissionManagerDialog from './dialogs/PermissionManagerDialog';
import DraftSaveDialog from './ItineraryCreator/DraftSaveDialog';
import QADialog from './dialogs/QADialog';
import { GoogleDocsExportDialog } from './dialogs/GoogleDocsExportDialog';

// Import feature modules
import TranslationModule from './features/TranslationModule';
import TourModule from './features/TourModule';
import { buildTranslationStatePayload, compareGuideSharedContent, compareGuideTranslationContent, getTranslationGroupId, syncSharedFieldsToGuide } from './features/linkedLanguageUtils';

// Import existing components
import { MetadataSection } from './ItineraryCreator/utils/MetadataSection';
import { MuseumInfoSection } from './ItineraryCreator/utils/MuseumInfoSection';
import VenueLandingPageSection from './ItineraryCreator/utils/VenueLandingPageSection';
import KnowledgeDocPanel from './ItineraryCreator/utils/KnowledgeDocPanel';
import { buildKnowledgeContext } from './ItineraryCreator/utils/knowledgeDocumentUtils';
import { parseBatchSupplementaryContentResponse } from './ItineraryCreator/utils/supplementaryContentParser';
import { EnhancedPOIForm } from './forms/EnhancedPOIForm';
import { POI_CATEGORIES } from './ItineraryCreator/index';
import { POIList } from './itinerary/POIList';
import { ContextSection } from './ItineraryCreator/tour/ContextSection';
import ItineraryMapView from './itinerary/ItineraryMapView';
import DraftManager from './ItineraryCreator/DraftManager';
import FirebaseExporterSection from './ItineraryCreator/storage/FirebaseExporterSection';
import ImageUploader from './ItineraryCreator/ui/ImageUploader';
import BatchAudioGenerator from './audio/BatchAudioGenerator';
import { buildAudioGenerationStamp } from './audio/audioGenerationInfo';
import GuidesListing from './GuidesListing';
import PublishedVenuesListing from './PublishedVenuesListing';
import RAGDocumentManager from './ItineraryCreator/rag/RAGDocumentManager';
import RagAndBlocksTab from './ItineraryCreator/rag/RagAndBlocksTab';
import StoryAITab from './ItineraryCreator/story/StoryAITab';
import AiGuideContentTab from './ItineraryCreator/aiGuideContent/AiGuideContentTab';
import MediaPackageTab from './ItineraryCreator/mediaPackage/MediaPackageTab';
import GuideQualityEvaluator from './ItineraryCreator/quality/GuideQualityEvaluator';
import { ObjectsManager } from './MuseumObjects';
import { RoomRecognitionManager } from './RoomRecognition';
import { VprManager } from './VPR';
import ModelSelector from './selectors/ModelSelector';
import { CheckCircle, AlertTriangle, Loader, ChevronDown, ChevronUp, MapPin, Check, AlertCircle } from 'lucide-react';
import { computeSingleRouteLeg, DEFAULT_ROUTE_MODE, ROUTE_MODE_OPTIONS } from './services/routeLegService';
import { buildTranslationStateUrl, saveRoutesToStorage, saveTranslationStateToStorage } from './ItineraryCreator/utils/storageUtils';
import { generateSafeGuideFilename, generateSafeFolderName, sanitizeFilename } from './ItineraryCreator/utils/filenameSanitizer';
import { fetchGuideWithAuth } from '../utils/authenticatedFetchGuide';
import { saveItineraryToStorage } from './ItineraryCreator/utils/storageUtils';
import { stripTranslatedPoiAudioFields } from './ItineraryCreator/utils/translationUtils';
import { AVAILABLE_VOICES } from './ItineraryCreator/utils/voiceConstants';
import { readWorkspaceUrlState, replaceWorkspaceUrl } from './utils/builderUrlState';

// Import utilities
import patchReactDOM from './ItineraryCreator/utils/DOMNodePatcher';
import { generateId, generatePoiId, getPointType, initialPoiState } from './ItineraryCreator/utils/utils';

// Import logo
import Logo_Circle from './ItineraryCreator/ui/Logo_Circle.png';

const extractStoragePathFromUrl = (url) => {
  if (!url) return '';

  try {
    const parsed = new URL(url);

    if (parsed.pathname.includes('/o/')) {
      const encodedPath = parsed.pathname.split('/o/')[1];
      return encodedPath ? decodeURIComponent(encodedPath) : '';
    }

    const directPathMatch = parsed.pathname.match(/\/special_itineraries\/(.+)$/);
    if (directPathMatch?.[1]) {
      return `special_itineraries/${decodeURIComponent(directPathMatch[1])}`;
    }
  } catch (err) {
    console.warn('Could not parse storage path from URL:', err.message);
  }

  return '';
};

const makeLegKey = (fromStopId, toStopId) => `${fromStopId}__${toStopId}`;

const getStopLatLng = (stop) => ([
  parseFloat(stop?.coordinates?.latitude),
  parseFloat(stop?.coordinates?.longitude),
]);

const haversineMeters = ([lat1, lng1], [lat2, lng2]) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const estimateDurationSeconds = (distanceMeters, routeMode) => {
  const speedKmh = {
    walking: 5,
    cycling: 15,
    driving: 35,
  }[routeMode] || 5;
  return Math.round((distanceMeters / 1000 / speedKmh) * 3600);
};

const buildManualLeg = (fromStop, toStop, draftPoints, routeMode) => {
  const polyline = [getStopLatLng(fromStop), ...draftPoints, getStopLatLng(toStop)];
  let distanceMeters = 0;

  for (let i = 0; i < polyline.length - 1; i++) {
    distanceMeters += haversineMeters(polyline[i], polyline[i + 1]);
  }

  return {
    from_stop_id: fromStop.id,
    to_stop_id: toStop.id,
    route_mode: routeMode,
    route_source: 'manual',
    ors_profile: null,
    distance_meters: Math.round(distanceMeters),
    duration_seconds: estimateDurationSeconds(distanceMeters, routeMode),
    polyline,
    steps: [],
  };
};

const makeExtraLegId = () => `extra_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const getLegStorageKey = (leg) => {
  if (leg?.is_extra) return leg.id || makeExtraLegId();
  return makeLegKey(leg?.from_stop_id, leg?.to_stop_id);
};

const getOverlayAspectRatio = (overlay) => {
  const ratio = parseFloat(overlay?.aspectRatio);
  return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
};

const getOverlayCenter = (overlay, fallbackCenter = [38.7223, -9.1393]) => {
  if (Array.isArray(overlay?.center) && overlay.center.length === 2) {
    const lat = parseFloat(overlay.center[0]);
    const lng = parseFloat(overlay.center[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lat, lng];
    }
  }
  return fallbackCenter;
};

const isPopupStopPoint = (point) => getPointType(point) === 'popup_stop';

const getOverlayWidthMeters = (overlay, fallbackWidthMeters = 400) => {
  const widthMeters = parseFloat(overlay?.widthMeters);
  return Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : fallbackWidthMeters;
};

const computeViewportWidthMeters = (bounds) => {
  if (!Array.isArray(bounds) || bounds.length !== 2) return null;

  const south = parseFloat(bounds[0]?.[0]);
  const west = parseFloat(bounds[0]?.[1]);
  const north = parseFloat(bounds[1]?.[0]);
  const east = parseFloat(bounds[1]?.[1]);

  if ([south, west, north, east].some((value) => !Number.isFinite(value))) {
    return null;
  }

  const midLat = (south + north) / 2;
  return haversineMeters([midLat, west], [midLat, east]);
};

const GuideBuilder = () => {
  const { currentUser, userRole, logout } = useAuth();
  const { hasFieldAccess, getFieldAccess } = useAccessControl();
  const { fetchGuides, getCachedGuides } = useGuides();
  const { language } = useLanguage();

  // Apply DOM patches on component mount
  useEffect(() => {
    patchReactDOM();
  }, []);

  // Initialize state hooks
  const itineraryState = useItineraryState(language);
  const itineraryRef = useRef(itineraryState.itinerary);
  itineraryRef.current = itineraryState.itinerary;
  const uiState = useUIState();
  const draftState = useDraftState();
  const translationState = useTranslationState();
  const routeMetadata = itineraryState.itinerary?.metadata || {};
  const routeFilename = routeMetadata.filename;
  const routeName = routeMetadata.name;
  const routeCountry = routeMetadata.country;
  const isVenueEditingContext = routeMetadata.editor_mode === 'venue';
  const sourceGuideUrl = itineraryState.sourceGuideUrl;
  const shouldWarnOnPoiExit = itineraryState.hasUnsavedChanges
    && uiState.activeTab === 'edit-poi'
    && (!itineraryState.isUpdateMode || !itineraryState.currentPoi?.id);
  const browserBackWarningMessage = 'You have progress in the builder. Are you sure you want to leave this page?';

  // Warn user if they try to leave with unsaved POI changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (shouldWarnOnPoiExit) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldWarnOnPoiExit]);

  useEffect(() => {
    window.history.pushState({ guideBuilderGuard: true }, '', window.location.href);

    const handlePopState = () => {
      const shouldLeave = window.confirm(browserBackWarningMessage);
      if (!shouldLeave) {
        window.history.pushState({ guideBuilderGuard: true }, '', window.location.href);
        return;
      }

      window.removeEventListener('popstate', handlePopState);
      window.history.back();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // QA Check state
  const [qaResults, setQaResults] = useState(null);
  const [qaType, setQaType] = useState(null); // 'guide' | 'museum'
  const [isQAChecking, setIsQAChecking] = useState(false);
  const [qualityEvaluation, setQualityEvaluation] = useState(null);

  // Rewrite state
  const [isRewriting, setIsRewriting] = useState(false);
  const [isGeneratingDescriptions, setIsGeneratingDescriptions] = useState(false);
  const [isGeneratingKnowledgeBatch, setIsGeneratingKnowledgeBatch] = useState(false);
  const [knowledgeBatchStatus, setKnowledgeBatchStatus] = useState(null);
  const [knowledgeBatchMessage, setKnowledgeBatchMessage] = useState('');

  // QA Section collapse state
  const [qaExpanded, setQaExpanded] = useState(false);

  // Google Docs Export state
  const [showGoogleDocsExport, setShowGoogleDocsExport] = useState(false);
  const [linkedGuideItems, setLinkedGuideItems] = useState([]);
  const [selectedLinkedGuideIds, setSelectedLinkedGuideIds] = useState([]);
  const [isLoadingLinkedGuides, setIsLoadingLinkedGuides] = useState(false);
  const [isSavingLinkedGuides, setIsSavingLinkedGuides] = useState(false);
  const [linkedGuidesError, setLinkedGuidesError] = useState('');

  // Route legs — kept separate from the main itinerary JSON
  const [routeLegs, setRouteLegs] = useState([]);
  const [savedRoutesFileStatus, setSavedRoutesFileStatus] = useState('unknown');
  const [savedRoutesFilePath, setSavedRoutesFilePath] = useState('');
  const [extraRouteLegs, setExtraRouteLegs] = useState([]);
  const [routeMode, setRouteMode] = useState('walking');
  const [legRouteModes, setLegRouteModes] = useState({});
  const [manualEditLegKey, setManualEditLegKey] = useState(null);
  const [manualEditExtraLegId, setManualEditExtraLegId] = useState(null);
  const [manualDraftPoints, setManualDraftPoints] = useState([]);
  const [isComputingLegs, setIsComputingLegs] = useState(false);
  const [legsLog, setLegsLog] = useState([]);
  const [legsStatus, setLegsStatus] = useState(null); // 'success' | 'error' | null
  const [mapViewport, setMapViewport] = useState(null);
  const [overlayNudgeMeters, setOverlayNudgeMeters] = useState(25);
  const [isOverlayEditMode, setIsOverlayEditMode] = useState(false);
  const [isStopPlacementMode, setIsStopPlacementMode] = useState(false);
  const [isSequentialStopPlacementMode, setIsSequentialStopPlacementMode] = useState(false);
  const [isPopupStopPlacementMode, setIsPopupStopPlacementMode] = useState(false);
  const [selectedStopIdForMap, setSelectedStopIdForMap] = useState(null);
  const [isMapOverlayBuilderExpanded, setIsMapOverlayBuilderExpanded] = useState(false);
  const [isRouteLegsExpanded, setIsRouteLegsExpanded] = useState(false);
  const [isPerLegRouteControlsExpanded, setIsPerLegRouteControlsExpanded] = useState(false);
  const legsLogEndRef = useRef(null);
  const hasHandledExternalGuideLoadRef = useRef(false);

  // Initialize custom hooks
  const { generatePoiAudio } = useAudioGeneration();
  const { generateFieldContent, isGenerating } = useFieldGeneration();
  const { handleImageUpload } = useImageUpload();
  const { generateContent } = useItineraryContent();
  const { generateTourPlan } = useTourGeneration();
  const { currentModel: selectedModel, changeModel } = useAiModel();
  const { generatePromptForField } = usePromptTemplates();

  const isBusTourGuide = itineraryState.itinerary.metadata?.guide_type === 'bus_tour';
  const narratedPoints = useMemo(
    () => (itineraryState.itinerary.points || []).filter((point) => !isPopupStopPoint(point)),
    [itineraryState.itinerary.points]
  );
  const popupStopPoints = useMemo(
    () => (itineraryState.itinerary.points || []).filter((point) => isPopupStopPoint(point)),
    [itineraryState.itinerary.points]
  );
  const reviewSummary = useMemo(() => {
    const metadata = itineraryState.itinerary?.metadata || {};
    const points = itineraryState.itinerary?.points || [];
    const metadataIssues = [];
    const routeIssues = [];
    const stopFieldCounts = {};
    const incompleteStops = [];
    const audioMissingStops = [];
    const audioLanguageIssueStops = [];
    const imageMissingStops = [];
    const hasValue = (value) => {
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.some((item) => String(item || '').trim() !== '');
      return value !== undefined && value !== null && value !== '';
    };
    const hasCoordinates = (coordinates) => (
      hasValue(coordinates?.latitude) && hasValue(coordinates?.longitude)
    );
    const normalizeLanguage = (value) => String(value || '').trim().toLowerCase().replace('_', '-');
    const languagesMatch = (guideLang, voiceLang) => {
      if (!guideLang || !voiceLang) return false;
      if (guideLang === voiceLang) return true;
      return guideLang.split('-')[0] === voiceLang.split('-')[0];
    };
    const guideLanguage = normalizeLanguage(metadata.language);
    const requiresSavedRoutes = narratedPoints.length > 1;

    if (!/^\d{13}$/.test(String(metadata.id || '').trim())) {
      metadataIssues.push('Guide ID must be a 13-digit number like 1780599465639.');
    }
    if (getFieldAccess('itinerary_name') !== 'hidden' && !hasValue(metadata.name)) metadataIssues.push('Guide name');
    if (getFieldAccess('theme') !== 'hidden' && !hasValue(metadata.theme)) metadataIssues.push('Theme');
    if (getFieldAccess('country') !== 'hidden' && !hasValue(metadata.country)) metadataIssues.push('Country');
    if (getFieldAccess('city') !== 'hidden' && !hasValue(metadata.city)) metadataIssues.push('City');
    if (getFieldAccess('data_source') !== 'hidden' && !hasValue(metadata.data_source)) metadataIssues.push('Data source');
    if (getFieldAccess('language') !== 'hidden' && !hasValue(metadata.language)) metadataIssues.push('Content language');
    if (getFieldAccess('itinerary_duration') !== 'hidden' && !hasValue(metadata.duration)) metadataIssues.push('Duration');
    if (getFieldAccess('itinerary_difficulty') !== 'hidden' && !hasValue(metadata.difficulty)) metadataIssues.push('Difficulty');
    if (getFieldAccess('itinerary_description') !== 'hidden' && !hasValue(metadata.description)) metadataIssues.push('Description');
    if (getFieldAccess('thumbnailUrl') !== 'hidden' && !hasValue(metadata.thumbnailUrl)) metadataIssues.push('Thumbnail image');
    if (getFieldAccess('itinerary_coordinates') !== 'hidden' && !hasCoordinates(metadata.coordinates)) metadataIssues.push('Guide coordinates');
    if (getFieldAccess('itinerary_categories') !== 'hidden' && !hasValue(metadata.categories)) metadataIssues.push('Categories');
    if (getFieldAccess('itinerary_tags') !== 'hidden' && !hasValue(metadata.tags)) metadataIssues.push('Tags');
    if (getFieldAccess('isIntroductoryStop') !== 'hidden' && (metadata.isIntroductoryStop === undefined || metadata.isIntroductoryStop === null)) {
      metadataIssues.push('Introduction by Sofia setting');
    }
    if (getFieldAccess('guide_type') !== 'hidden' && !hasValue(metadata.guide_type)) metadataIssues.push('Guide type');
    if (metadata['360visit'] === true && !hasValue(metadata['360visitUrl'])) metadataIssues.push('360° visit URL');
    if (requiresSavedRoutes && savedRoutesFileStatus !== 'present') {
      routeIssues.push('Saved routes file is missing.');
    }

    points.forEach((point, index) => {
      const isPopupStop = getPointType(point) === 'popup_stop';
      const missingFields = [];
      const stopLabel = point.name || `Stop ${index + 1}`;
      const hasAudio = hasValue(point.audioUrl) || hasValue(point.uncompressedAudioUrl) || hasValue(point.originalAudioUrl);
      const voiceLanguage = normalizeLanguage(AVAILABLE_VOICES[point.ttsVoice]?.languageCode);
      const pointLanguage = normalizeLanguage(point.language);
      const effectiveAudioLanguage = voiceLanguage || pointLanguage;

      if (!hasValue(point.name)) missingFields.push('Name');
      if (!hasValue(point.category)) missingFields.push('Category');
      if (!hasCoordinates(point.coordinates)) missingFields.push('Coordinates');
      if (!isPopupStop && !hasValue(point.inDepthDescription)) missingFields.push('In-depth description');
      if (isPopupStop) {
        if (guideLanguage && !languagesMatch(guideLanguage, pointLanguage)) {
          audioLanguageIssueStops.push({
            id: point.id || `stop-${index}`,
            label: stopLabel,
            detail: `Popup stop language is ${pointLanguage || 'missing'}, guide language is ${guideLanguage}.`,
          });
        }
      } else {
        if (!hasValue(point.photoUrl)) imageMissingStops.push({
          id: point.id || `stop-${index}`,
          label: stopLabel,
        });

        if (!hasAudio) {
          audioMissingStops.push({
            id: point.id || `stop-${index}`,
            label: stopLabel,
          });
        } else if (guideLanguage && effectiveAudioLanguage && !languagesMatch(guideLanguage, effectiveAudioLanguage)) {
          audioLanguageIssueStops.push({
            id: point.id || `stop-${index}`,
            label: stopLabel,
            detail: `Audio language is ${effectiveAudioLanguage}, guide language is ${guideLanguage}.`,
          });
        }
      }

      if (missingFields.length > 0) {
        incompleteStops.push({
          id: point.id || `stop-${index}`,
          label: stopLabel,
          missingFields,
        });

        missingFields.forEach((field) => {
          stopFieldCounts[field] = (stopFieldCounts[field] || 0) + 1;
        });
      }
    });

    return {
      hasValidGuideId: /^\d{13}$/.test(String(metadata.id || '').trim()),
      metadataIssues,
      routeIssues,
      requiresSavedRoutes,
      savedRoutesFileStatus,
      savedRoutesFilePath,
      stopFieldCounts,
      incompleteStops,
      audioMissingStops,
      audioLanguageIssueStops,
      imageMissingStops,
      totalIssueCount:
        metadataIssues.length +
        routeIssues.length +
        incompleteStops.reduce((sum, stop) => sum + stop.missingFields.length, 0) +
        audioMissingStops.length +
        audioLanguageIssueStops.length +
        imageMissingStops.length,
    };
  }, [getFieldAccess, itineraryState.itinerary, narratedPoints.length, savedRoutesFilePath, savedRoutesFileStatus]);

  const routePlans = useMemo(() => {
    const points = narratedPoints;
    const legsByKey = new Map(
      routeLegs
        .filter((leg) => !leg.is_extra)
        .map((leg) => [makeLegKey(leg.from_stop_id, leg.to_stop_id), leg])
    );

    return points.slice(0, -1).map((fromStop, index) => {
      const toStop = points[index + 1];
      const key = makeLegKey(fromStop.id, toStop.id);
      const existingLeg = legsByKey.get(key) || null;
      return {
        key,
        index,
        fromStop,
        toStop,
        existingLeg,
        selectedMode: legRouteModes[key] || existingLeg?.route_mode || routeMode || DEFAULT_ROUTE_MODE,
      };
    });
  }, [narratedPoints, routeLegs, legRouteModes, routeMode]);

  const extraLegRows = useMemo(() => extraRouteLegs, [extraRouteLegs]);

  const activeManualPlan = routePlans.find((plan) => plan.key === manualEditLegKey) || null;
  const activeManualExtraLeg = extraLegRows.find((leg) => leg.id === manualEditExtraLegId) || null;
  const activeManualTarget = activeManualPlan || activeManualExtraLeg || null;
  const activeManualDraftPolyline = activeManualTarget
    ? [getStopLatLng(activeManualTarget.fromStop), ...manualDraftPoints, getStopLatLng(activeManualTarget.toStop)]
    : [];
  const selectedStopForMap = narratedPoints.find((point) => point.id === selectedStopIdForMap) || null;
  const mapOverlay = itineraryState.itinerary.metadata?.mapOverlay || {};
  const fallbackOverlayCenter = Array.isArray(mapViewport?.center)
    ? mapViewport.center
    : [
        parseFloat(itineraryState.itinerary.metadata?.coordinates?.latitude) || 38.7223,
        parseFloat(itineraryState.itinerary.metadata?.coordinates?.longitude) || -9.1393,
      ];
  const mapOverlayCenter = getOverlayCenter(mapOverlay, fallbackOverlayCenter);
  const mapOverlayAspectRatio = getOverlayAspectRatio(mapOverlay);
  const mapOverlayWidthMeters = getOverlayWidthMeters(
    mapOverlay,
    computeViewportWidthMeters(mapViewport?.bounds) || 400
  );

  useEffect(() => {
    if (!isStopPlacementMode) return;
    if (selectedStopIdForMap && narratedPoints.some((point) => point.id === selectedStopIdForMap)) {
      return;
    }

    const firstUnplaced = narratedPoints.find((point) => {
      const lat = parseFloat(point?.coordinates?.latitude);
      const lng = parseFloat(point?.coordinates?.longitude);
      return !Number.isFinite(lat) || !Number.isFinite(lng);
    });

    setSelectedStopIdForMap(firstUnplaced?.id || narratedPoints[0]?.id || null);
  }, [isStopPlacementMode, selectedStopIdForMap, narratedPoints]);

  useEffect(() => {
    if (!isSequentialStopPlacementMode) return;
    setIsStopPlacementMode(true);
  }, [isSequentialStopPlacementMode]);

  useEffect(() => {
    const nextModes = {};
    routePlans.forEach((plan) => {
      nextModes[plan.key] = plan.selectedMode;
    });
    setLegRouteModes((prev) => {
      const merged = {};
      Object.entries(nextModes).forEach(([key, value]) => {
        merged[key] = prev[key] || value;
      });

      const previousKeys = Object.keys(prev);
      const mergedKeys = Object.keys(merged);
      const isUnchanged = previousKeys.length === mergedKeys.length
        && mergedKeys.every((key) => prev[key] === merged[key]);

      if (isUnchanged) return prev;
      return merged;
    });
  }, [routePlans]);

  // Create wrapper function for field generation
  const handleGenerateField = async (field, customPrompt = null) => {
    try {
      console.log(`Generating field: ${field} for ${itineraryState.currentPoi.name || 'new POI'}`);
      console.log(`Using AI model: ${selectedModel}`);
      console.log(`Generating in language: ${language}`);

      if (customPrompt) {
        console.log(`Using provided custom prompt for ${field}`);

        let finalPrompt = customPrompt;
        if (!finalPrompt.includes(language)) {
          const languageInstruction = ` Please respond in ${language}.`;
          finalPrompt += languageInstruction;
        }

        const generatedContent = await generateFieldContent(field, itineraryState.currentPoi, {
          city: itineraryState.itinerary.metadata.city,
          country: itineraryState.itinerary.metadata.country,
          theme: itineraryState.itinerary.metadata.theme,
          language: language,
          prompt: finalPrompt
        }, selectedModel);

        // Update the POI with generated content
        const updatedPoi = {
          ...itineraryState.currentPoi,
          [field]: generatedContent,
          language: language
        };

        // Update both form state AND itinerary state (so it persists when switching tabs)
        itineraryState.setCurrentPoi(updatedPoi);

        // If editing existing POI, update it in the itinerary state too
        if (itineraryState.isUpdateMode && updatedPoi.id) {
          itineraryState.updatePoi(updatedPoi.id, updatedPoi);
        }

        // Mark as having unsaved changes (to file)
        itineraryState.setHasUnsavedChanges(true);

        return;
      }

      console.error(`No custom prompt provided for field: ${field}. This should not happen.`);

      const languageInstruction = ` Please respond in ${language}.`;
      let fieldPrompt = `Generate ${field} for point of interest "${itineraryState.currentPoi.name || 'New POI'}" in ${itineraryState.itinerary.metadata.city}, ${itineraryState.itinerary.metadata.country}. Theme: ${itineraryState.itinerary.metadata.theme}.${languageInstruction}`;

      const generatedContent = await generateFieldContent(field, itineraryState.currentPoi, {
        city: itineraryState.itinerary.metadata.city,
        country: itineraryState.itinerary.metadata.country,
        theme: itineraryState.itinerary.metadata.theme,
        language: language,
        prompt: fieldPrompt
      }, selectedModel);

      itineraryState.setCurrentPoi(prev => ({
        ...prev,
        [field]: generatedContent,
        language: language
      }));
    } catch (error) {
      console.error(`Error in handleGenerateField for field ${field}:`, error);
      itineraryState.setErrors(prev => ({
        ...prev,
        [field]: `Failed to generate content: ${error.message}`
      }));
    }
  };

  // Batch field generation for all POIs
  const handleBatchFieldGeneration = async () => {
    const fieldsToGenerate = ['historicalContext', 'description', 'culturalSignificance', 'hiddenGems'];
    const pois = itineraryState.itinerary.points;

    if (pois.length === 0) {
      alert('No points of interest to generate content for.');
      return;
    }

    const confirmed = window.confirm(
      `Generate content for ${pois.length} POIs?\n\n` +
      `This will generate:\n` +
      `• Historical Context\n` +
      `• Brief Description\n` +
      `• Cultural Significance\n` +
      `• Hidden Gems\n\n` +
      `Total fields: ${pois.length * fieldsToGenerate.length}\n\n` +
      `This may take several minutes.`
    );

    if (!confirmed) return;

    const stats = {
      total: pois.length * fieldsToGenerate.length,
      successful: 0,
      failed: 0,
      startTime: Date.now()
    };

    console.log('🚀 Starting batch field generation...');

    for (let poiIndex = 0; poiIndex < pois.length; poiIndex++) {
      const poi = pois[poiIndex];
      console.log(`\n📍 Processing POI ${poiIndex + 1}/${pois.length}: ${poi.name}`);

      for (const field of fieldsToGenerate) {
        try {
          console.log(`  ⏳ Generating ${field}...`);

          // Generate prompt for this field
          const filledPrompt = generatePromptForField(field, poi, itineraryState.itinerary.metadata);

          // Generate content
          const generatedContent = await generateFieldContent(field, poi, {
            city: itineraryState.itinerary.metadata.city,
            country: itineraryState.itinerary.metadata.country,
            theme: itineraryState.itinerary.metadata.theme,
            language: language,
            prompt: filledPrompt
          }, selectedModel);

          // Update POI in itinerary state
          itineraryState.updatePoi(poi.id, {
            ...poi,
            [field]: generatedContent
          });

          stats.successful++;
          console.log(`  ✅ ${field} generated`);

        } catch (error) {
          stats.failed++;
          console.error(`  ❌ Failed to generate ${field}:`, error.message);
        }
      }
    }

    stats.totalTime = ((Date.now() - stats.startTime) / 1000).toFixed(1);
    console.log('🎊 Batch field generation complete:', stats);

    // Mark as having unsaved changes
    itineraryState.setHasUnsavedChanges(true);

    if (stats.successful > 0) {
      alert(
        `🎉 Batch field generation complete!\n\n` +
        `✅ ${stats.successful}/${stats.total} fields generated successfully\n` +
        `❌ ${stats.failed} failed\n` +
        `⏱️ Total time: ${stats.totalTime}s\n\n` +
        `Remember to save your changes!`
      );
    } else {
      alert(`❌ Batch field generation failed.\n\nNo fields were generated successfully. Please check the logs for details.`);
    }
  };

  // Helper function to safely switch tabs with unsaved changes warning
  const handleSwitchTab = (tabName) => {
    if (shouldWarnOnPoiExit) {
      const confirmed = window.confirm(
        `You have unsaved changes to "${itineraryState.currentPoi?.name || 'this point of interest'}". Are you sure you want to discard them?`
      );
      if (!confirmed) return;
    }
    uiState.setActiveTab(tabName);
    replaceWorkspaceUrl({ section: 'builder', tab: tabName });
  };

  // Show the Venue Page tab once the guide's Business Page is enabled.
  // Previously this checked whether the tab was already active, making it
  // impossible to select from the normal tab navigation.
  const showVenuePageTab = Boolean(itineraryState.itinerary?.metadata?.museum_infos);

  // Audio generation handler for individual POI
  const handleGeneratePoiAudio = async (poi, voiceId) => {
    console.log('[handleGeneratePoiAudio] Generating audio for POI:', poi.name);

    if (!poi.inDepthDescription) {
      itineraryState.setErrors(prev => ({
        ...prev,
        audio: 'Please generate or enter an in-depth description first'
      }));
      return;
    }

    try {
      const audioResult = await generatePoiAudio(
        poi.inDepthDescription,
        poi,
        itineraryState.itinerary.metadata,
        voiceId
      );

      if (audioResult && audioResult.audioUrl) {
        const audioStamp = buildAudioGenerationStamp(voiceId, audioResult);
        // Update current POI
        itineraryState.setCurrentPoi(prev => ({
          ...prev,
          audioUrl: audioResult.audioUrl,
          uncompressedAudioUrl: audioResult.uncompressedAudioUrl || audioResult.originalAudioUrl || prev.uncompressedAudioUrl || '',
          originalAudioUrl: audioResult.originalAudioUrl || audioResult.uncompressedAudioUrl || prev.originalAudioUrl || '',
          compressedAudioVariants: Array.isArray(audioResult.compressedAudioVariants) ? audioResult.compressedAudioVariants : prev.compressedAudioVariants,
          ttsVoice: voiceId,
          ...audioStamp,
          estimatedDuration: audioResult.audioDuration
            ? Math.ceil(audioResult.audioDuration / 60)
            : prev.estimatedDuration
        }));

        // Update POI in itinerary list if it exists
        if (poi.id) {
          itineraryState.setItinerary(prev => ({
            ...prev,
            points: prev.points.map(point =>
              point.id === poi.id
                ? {
                    ...point,
                    audioUrl: audioResult.audioUrl,
                    uncompressedAudioUrl: audioResult.uncompressedAudioUrl || audioResult.originalAudioUrl || point.uncompressedAudioUrl || '',
                    originalAudioUrl: audioResult.originalAudioUrl || audioResult.uncompressedAudioUrl || point.originalAudioUrl || '',
                    compressedAudioVariants: Array.isArray(audioResult.compressedAudioVariants) ? audioResult.compressedAudioVariants : point.compressedAudioVariants,
                    ttsVoice: voiceId,
                    ...audioStamp,
                    estimatedDuration: audioResult.audioDuration
                      ? Math.ceil(audioResult.audioDuration / 60)
                      : point.estimatedDuration
                  }
                : point
            )
          }));
        }

        return audioResult.audioUrl;
      }
    } catch (error) {
      console.error('[handleGeneratePoiAudio] Error:', error);
      itineraryState.setErrors(prev => ({
        ...prev,
        audio: `Failed to generate audio: ${error.message}`
      }));
      throw error;
    }
  };

  // Handler for individual POI audio completion during batch generation
  const handleIndividualAudioComplete = (updatedPoi, currentIndex, totalPois) => {
    console.log(`🔔 Individual audio complete for POI ${currentIndex}/${totalPois}: ${updatedPoi.name}`);

    itineraryState.setItinerary(prev => ({
      ...prev,
      points: prev.points.map(poi =>
        poi.id === updatedPoi.id
          ? { ...poi, ...updatedPoi }
          : poi
      ),
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));
  };

  // Handler for batch audio generation completion
  const handleBatchAudioComplete = async (updatedPois, stats) => {
    console.log('🎊 Batch audio generation complete:', stats);
    const operation = stats?.mode || 'generation';

    itineraryState.setItinerary(prev => {
      const updatedItinerary = {
        ...prev,
        points: prev.points.map(originalPoi => {
          const updatedPoi = updatedPois.find(updated => updated.id === originalPoi.id);
          return updatedPoi || originalPoi;
        }),
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString(),
          ...(operation === 'conversion'
            ? {
                lastAudioConversion: {
                  timestamp: new Date().toISOString(),
                  language: updatedPois[0]?.language || language,
                  voice: updatedPois[0]?.ttsVoice,
                  stats: stats
                }
              }
            : {
                lastAudioGeneration: {
                  timestamp: new Date().toISOString(),
                  language: updatedPois[0]?.language || language,
                  voice: updatedPois[0]?.ttsVoice,
                  stats: stats
                }
              })
        }
      };

      return updatedItinerary;
    });

      if (stats.successful > 0) {
        if (operation === 'conversion') {
          alert(`🎉 Batch audio conversion complete!\n\n✅ ${stats.successful}/${stats.total} audio files converted successfully\n⏱️ Total time: ${stats.totalTime}s`);
        } else {
          alert(`🎉 Batch audio generation complete!\n\n✅ ${stats.successful}/${stats.total} audio files generated successfully\n⏱️ Total time: ${stats.totalTime}s`);
        }
      } else {
        alert(`❌ Batch audio ${operation === 'conversion' ? 'conversion' : 'generation'} failed.\n\nNo audio files were processed successfully. Please check the logs for details.`);
      }
  };

  // Handler for translate all itinerary
  const handleTranslateAll = async () => {
    const { targetLanguage, translationModel } = translationState;
    const { itinerary } = itineraryState;

    // Check if there's content to translate (using correct structure: metadata.name, points)
    if (!itinerary.metadata?.name && !itinerary.metadata?.description && (!itinerary.points || itinerary.points.length === 0)) {
      alert('Please add some content to translate first.');
      return;
    }

    translationState.setIsTranslatingAll(true);
    translationState.clearTranslationProgress();

    try {
      const currentLanguage = String(itinerary.metadata?.language || '').toLowerCase();
      const normalizedTargetLanguage = String(targetLanguage || '').toLowerCase();
      if (currentLanguage === normalizedTargetLanguage) {
        throw new Error('Target language matches the current guide language.');
      }

      console.log(`Starting full itinerary translation to ${targetLanguage} using ${translationModel}...`);

      // Map itinerary structure to what aiService expects (title, description, pois)
      const itineraryForTranslation = {
        ...itinerary,
        title: itinerary.metadata?.name || '',
        description: itinerary.metadata?.description || '',
        pois: itinerary.points || []
      };

      const translatedItinerary = await aiService.translateItineraryJSON(
        itineraryForTranslation,
        targetLanguage,
        translationModel,
        null,
        (progressMessage) => {
          translationState.addTranslationProgress(progressMessage);
        }
      );

      const rootGuideId = itinerary.metadata?.parent_guide_id || itinerary.metadata?.id || generateId();
      const translatedGuideId = generateId();
      const translatedLanguage = targetLanguage;
      const translatedLanguageShort = String(translatedLanguage || 'en').toLowerCase().split(/[-_]/)[0] || 'en';
      const translatedDisplayName = translatedItinerary.metadata?.name || translatedItinerary.title;
      const translatedDescription = translatedItinerary.metadata?.description || translatedItinerary.description;
        // Map back to original structure (metadata.name, points)
        const finalItinerary = {
          ...translatedItinerary,
          metadata: {
          ...itinerary.metadata,
          ...translatedItinerary.metadata,
          id: translatedGuideId,
          parent_guide_id: rootGuideId,
          name: translatedDisplayName,
          description: translatedDescription,
          language: translatedLanguage,
            translated_itinerary: 'yes',
            filename: generateSafeGuideFilename({
              ...itinerary.metadata,
              ...translatedItinerary.metadata,
              name: translatedDisplayName,
              language: translatedLanguage
            }),
            translationSourceGuideId: itinerary.metadata?.id || rootGuideId,
            translationSourceLanguage: itinerary.metadata?.language || 'en-us',
            translationModel,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
        points: (translatedItinerary.pois || []).map((point, index) => ({
          ...stripTranslatedPoiAudioFields(point),
          id: `${rootGuideId}_${index + 1}_${translatedLanguageShort}`,
          language: translatedLanguage
        }))
      };

      // Remove temporary properties
      delete finalItinerary.title;
      delete finalItinerary.pois;

      itineraryState.setItinerary(finalItinerary);
      itineraryState.setSourceGuideUrl('');

      alert(`Translation complete.\n\nA new translated guide version was created for ${targetLanguage}.\n\nNew guide ID: ${translatedGuideId}\nParent guide ID: ${rootGuideId}`);
    } catch (error) {
      console.error('Translation failed:', error);
      alert(`❌ Translation failed: ${error.message}`);
    } finally {
      translationState.setIsTranslatingAll(false);
    }
  };

  // Handler for translate individual field
  const handleTranslateField = async (field) => {
    const { targetLanguage, translationModel } = translationState;
    const { itinerary } = itineraryState;

    let textToTranslate = '';

    if (field === 'name' || field === 'title') {
      textToTranslate = itinerary.metadata?.name;
    } else if (field === 'description') {
      textToTranslate = itinerary.metadata?.description;
    }

    if (!textToTranslate) {
      alert(`No content to translate for ${field}`);
      return;
    }

    translationState.startFieldTranslation(field);

    try {
      console.log(`Translating ${field} to ${targetLanguage}...`);

      const translatedText = await aiService.translateText(
        textToTranslate,
        targetLanguage,
        translationModel
      );

      itineraryState.setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [field === 'title' ? 'name' : field]: translatedText,
          language: targetLanguage,
          updatedAt: new Date().toISOString()
        }
      }));

      console.log(`✅ ${field} translated successfully`);
    } catch (error) {
      console.error(`Translation of ${field} failed:`, error);
      alert(`❌ Translation failed: ${error.message}`);
    } finally {
      translationState.endFieldTranslation(field);
    }
  };

  // Phase 2: Handler for translating POI in-depth details
  const handleTranslatePoiDetails = async (poiId) => {
    const { targetLanguage, translationModel } = translationState;
    const { itinerary } = itineraryState;

    const poi = itinerary.points.find(p => p.id === poiId);
    if (!poi) {
      alert('POI not found');
      return;
    }

    translationState.startFieldTranslation(`poi-details-${poiId}`);

    try {
      console.log(`Translating in-depth details for: ${poi.name}...`);

      const translatedPoi = await aiService.translatePoiDetailsJSON(
        poi,
        targetLanguage,
        translationModel
      );

      // Update the POI in the itinerary
      itineraryState.setItinerary(prev => ({
        ...prev,
        points: prev.points.map(p =>
          p.id === poiId ? translatedPoi : p
        ),
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      }));

      console.log(`✅ POI details translated successfully: ${poi.name}`);
      alert(`✅ Details translated for: ${poi.name}`);
    } catch (error) {
      console.error(`Translation of POI details failed:`, error);
      alert(`❌ Translation failed: ${error.message}`);
    } finally {
      translationState.endFieldTranslation(`poi-details-${poiId}`);
    }
  };

  // NEW: Handler for batch translating all POI details
  const handleTranslateAllPoiDetails = async () => {
    const { targetLanguage, translationModel } = translationState;
    const { itinerary } = itineraryState;

    const points = itinerary.points || [];

    if (points.length === 0) {
      alert('No points of interest to translate. Please add some POIs first.');
      return;
    }

    // Confirm before batch translation
    const confirmed = window.confirm(
      `Translate in-depth details for all ${points.length} POIs?\n\n` +
      `This will translate:\n` +
      `• In-depth descriptions\n` +
      `• Historical context\n` +
      `• Cultural significance\n` +
      `• Hidden gems\n` +
      `• Subtitles, Tips, and more\n\n` +
      `This may take several minutes.`
    );

    if (!confirmed) return;

    translationState.setIsTranslatingAll(true);
    translationState.clearTranslationProgress();

    try {
      console.log(`Starting batch POI details translation to ${targetLanguage}...`);

      const translatedPois = await aiService.translateAllPoiDetailsJSON(
        points,
        targetLanguage,
        translationModel,
        null,
        (progressMessage) => {
          translationState.addTranslationProgress(progressMessage);
        }
      );

      // Update all POIs in the itinerary
      itineraryState.setItinerary(prev => ({
        ...prev,
        points: translatedPois.map(stripTranslatedPoiAudioFields),
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      }));

      alert(`✅ Translation complete!\n\nAll ${points.length} POI details translated to ${targetLanguage} successfully.`);
    } catch (error) {
      console.error('Batch POI translation failed:', error);
      alert(`❌ Translation failed: ${error.message}`);
    } finally {
      translationState.setIsTranslatingAll(false);
    }
  };

  // Handler for download translation
  const handleDownloadTranslation = () => {
    const { itinerary } = itineraryState;
    const { targetLanguage } = translationState;

    const dataStr = JSON.stringify(prepareItineraryForPublication(itinerary), null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    const exportFileDefaultName = `itinerary_${targetLanguage}_${Date.now()}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const fetchTranslationStateForGuide = useCallback(async (guideUrl) => {
    const translationStateUrl = buildTranslationStateUrl(guideUrl);
    if (!translationStateUrl || !currentUser) {
      return null;
    }

    try {
      const response = await fetchGuideWithAuth(currentUser, translationStateUrl);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch translation state: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (String(error.message || '').includes('404')) {
        return null;
      }
      throw error;
    }
  }, [currentUser]);

  const loadLinkedGuides = useCallback(async (forceRefresh = false) => {
    const currentGuide = itineraryRef.current;
    const groupId = getTranslationGroupId(currentGuide);

    if (!currentUser || !groupId) {
      setLinkedGuideItems([]);
      setSelectedLinkedGuideIds([]);
      setLinkedGuidesError('');
      return;
    }

    setIsLoadingLinkedGuides(true);
    setLinkedGuidesError('');

    try {
      let guides = forceRefresh ? null : getCachedGuides('all');

      if (!guides) {
        const result = await fetchGuides('all', forceRefresh);
        guides = result.guides;
      }

      const siblingCatalogs = (guides || []).filter((guide) => {
        const candidateGroupId = String(guide.parent_guide_id || guide.ID || '').trim();
        return candidateGroupId === groupId && String(guide.ID) !== String(currentGuide?.metadata?.id);
      });

      if (siblingCatalogs.length === 0) {
        setLinkedGuideItems([]);
        setSelectedLinkedGuideIds([]);
        setLinkedGuidesError('No linked guides were found for this guide group. Check that the other language versions are published and share the same parent guide ID.');
        return;
      }

      const loadedGuides = await Promise.all(siblingCatalogs.map(async (catalog) => {
        if (!catalog.Website) {
          throw new Error(`Guide ${catalog.Name || catalog.ID} is missing a JSON URL.`);
        }

        const response = await fetchGuideWithAuth(currentUser, catalog.Website);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${catalog.Name || catalog.ID}: ${response.status}`);
        }

        const guideData = await response.json();
        const translationState = await fetchTranslationStateForGuide(catalog.Website);
        return {
          catalog,
          guideData,
          translationState,
          comparison: compareGuideSharedContent(currentGuide, guideData),
          translationComparison: compareGuideTranslationContent(currentGuide, guideData, translationState)
        };
      }));

      setLinkedGuideItems(loadedGuides);
      setSelectedLinkedGuideIds(loadedGuides.map((item) => String(item.catalog?.ID || item.guideData?.metadata?.id)));
    } catch (error) {
      console.error('Failed to load linked guides:', error);
      setLinkedGuidesError(error.message || 'Failed to load linked guides');
      setLinkedGuideItems([]);
      setSelectedLinkedGuideIds([]);
    } finally {
      setIsLoadingLinkedGuides(false);
    }
  }, [currentUser, fetchGuides, getCachedGuides, fetchTranslationStateForGuide]);

  const toggleLinkedGuideSelection = (guideId) => {
    setSelectedLinkedGuideIds((prev) => (
      prev.includes(guideId)
        ? prev.filter((id) => id !== guideId)
        : [...prev, guideId]
    ));
  };

  const setAllLinkedGuideSelections = (selectAll) => {
    setSelectedLinkedGuideIds(selectAll
      ? linkedGuideItems.map((item) => String(item.catalog?.ID || item.guideData?.metadata?.id))
      : []);
  };

  const syncSelectedLinkedGuides = () => {
    if (selectedLinkedGuideIds.length === 0) {
      alert('Select at least one linked language first.');
      return;
    }

    const confirmed = window.confirm(
      `Sync shared fields from the current guide to ${selectedLinkedGuideIds.length} linked language` +
      `${selectedLinkedGuideIds.length === 1 ? '' : 's'}?\n\n` +
      `This keeps translated text intact and updates shared fields like media, coordinates, tags, settings, and POI shared properties.`
    );

    if (!confirmed) return;

    const sourceGuide = itineraryState.itinerary;
    setLinkedGuideItems((prev) => prev.map((item) => {
      const guideId = String(item.catalog?.ID || item.guideData?.metadata?.id);
      if (!selectedLinkedGuideIds.includes(guideId)) {
        return item;
      }

      const syncedGuide = syncSharedFieldsToGuide(sourceGuide, item.guideData);
      return {
        ...item,
        guideData: syncedGuide,
        comparison: compareGuideSharedContent(sourceGuide, syncedGuide),
        translationComparison: compareGuideTranslationContent(sourceGuide, syncedGuide, item.translationState)
      };
    }));
  };

  const markSelectedLinkedGuidesTranslationReviewed = async () => {
    if (selectedLinkedGuideIds.length === 0) {
      alert('Select at least one linked language first.');
      return;
    }

    const confirmed = window.confirm(
      `Mark ${selectedLinkedGuideIds.length} linked language` +
      `${selectedLinkedGuideIds.length === 1 ? '' : 's'} as reviewed against the current guide text?\n\n` +
      `This updates the text-review status without overwriting translated content.`
    );

    if (!confirmed) return;

    setIsSavingLinkedGuides(true);
    setLinkedGuidesError('');

    try {
      const sourceGuide = itineraryState.itinerary;
      const selectedItems = linkedGuideItems.filter((item) => {
        const guideId = String(item.catalog?.ID || item.guideData?.metadata?.id);
        return selectedLinkedGuideIds.includes(guideId);
      });

      for (const item of selectedItems) {
        const translationState = buildTranslationStatePayload(sourceGuide, item.guideData);
        await saveTranslationStateToStorage(translationState, item.guideData.metadata || {});
      }

      alert(`Marked ${selectedItems.length} linked language ${selectedItems.length === 1 ? 'guide' : 'guides'} as translation-reviewed.`);
      await loadLinkedGuides(true);
    } catch (error) {
      console.error('Failed to mark linked guides as translation-reviewed:', error);
      setLinkedGuidesError(error.message || 'Failed to mark linked guides as translation-reviewed');
      alert(`Failed to mark linked guides as translation-reviewed: ${error.message}`);
    } finally {
      setIsSavingLinkedGuides(false);
    }
  };

  const saveSelectedLinkedGuides = async () => {
    if (selectedLinkedGuideIds.length === 0) {
      alert('Select at least one linked language first.');
      return;
    }

    setIsSavingLinkedGuides(true);
    setLinkedGuidesError('');

    try {
      const selectedItems = linkedGuideItems.filter((item) => {
        const guideId = String(item.catalog?.ID || item.guideData?.metadata?.id);
        return selectedLinkedGuideIds.includes(guideId);
      });

      for (const item of selectedItems) {
        await saveItineraryToStorage(item.guideData, item.guideData.metadata || {});
      }

      alert(`Saved ${selectedItems.length} linked language ${selectedItems.length === 1 ? 'guide' : 'guides'} successfully.`);
      await loadLinkedGuides(true);
    } catch (error) {
      console.error('Failed to save linked guides:', error);
      setLinkedGuidesError(error.message || 'Failed to save linked guides');
      alert(`Failed to save linked guides: ${error.message}`);
    } finally {
      setIsSavingLinkedGuides(false);
    }
  };

  const publishAiConfigToLinkedLanguages = async ({ sofiaAi, sofiaKnowledgePolicy }) => {
    if (linkedGuideItems.length === 0) return 0;

    setIsSavingLinkedGuides(true);
    setLinkedGuidesError('');
    try {
      const updatedItems = linkedGuideItems.map((item) => ({
        ...item,
        guideData: {
          ...item.guideData,
          metadata: {
            ...(item.guideData?.metadata || {}),
            sofia_knowledge_policy: sofiaKnowledgePolicy,
            sofia_ai: JSON.parse(JSON.stringify(sofiaAi)),
          },
        },
      }));

      for (const item of updatedItems) {
        await saveItineraryToStorage(item.guideData, item.guideData.metadata || {});
      }

      setLinkedGuideItems(updatedItems.map((item) => ({
        ...item,
        comparison: compareGuideSharedContent(itineraryState.itinerary, item.guideData),
        translationComparison: compareGuideTranslationContent(
          itineraryState.itinerary,
          item.guideData,
          item.translationState
        )
      })));
      return updatedItems.length;
    } catch (error) {
      console.error('Failed to publish AI settings to linked languages:', error);
      setLinkedGuidesError(error.message || 'Failed to publish AI settings to linked languages');
      throw error;
    } finally {
      setIsSavingLinkedGuides(false);
    }
  };

  useEffect(() => {
    const groupId = getTranslationGroupId(itineraryState.itinerary);

    if (!currentUser || !groupId) {
      setLinkedGuideItems([]);
      setSelectedLinkedGuideIds([]);
      setLinkedGuidesError('');
      return;
    }

    loadLinkedGuides(false);
  }, [
    currentUser,
    itineraryState.itinerary?.metadata?.id,
    itineraryState.itinerary?.metadata?.parent_guide_id,
    loadLinkedGuides
  ]);

  useEffect(() => {
    if (linkedGuideItems.length === 0) return;

      setLinkedGuideItems((prev) => prev.map((item) => ({
        ...item,
        comparison: compareGuideSharedContent(itineraryState.itinerary, item.guideData),
        translationComparison: compareGuideTranslationContent(itineraryState.itinerary, item.guideData, item.translationState)
      })));
  }, [
    itineraryState.itinerary,
    linkedGuideItems.length
  ]);

  // QA Check handlers
  const handleGuideQA = async () => {
    const points = itineraryState.itinerary.points || [];

    // Validate that there are points to check
    if (points.length === 0) {
      alert('No points of interest to check. Please add some POIs first.');
      return;
    }

    // Filter points with inDepthDescription
    const validPoints = points.filter(p => p.inDepthDescription && p.inDepthDescription.trim().length > 0);

    if (validPoints.length === 0) {
      alert('No in-depth descriptions found. Please add descriptions to your POIs first.');
      return;
    }

    setQaType('guide');
    uiState.setShowQADialog(true);
    setIsQAChecking(true);
    setQaResults({ totalPois: validPoints.length });

    try {
      const results = await qaService.analyzeGuideConsistency(validPoints, selectedModel);
      setQaResults(results);
    } catch (error) {
      console.error('Guide QA check failed:', error);
      setQaResults({
        analysis: `❌ QA check failed: ${error.message}\n\nPlease try again or contact support if the issue persists.`,
        totalPois: validPoints.length
      });
    } finally {
      setIsQAChecking(false);
    }
  };

  const handleMuseumQA = async () => {
    const points = itineraryState.itinerary.points || [];

    if (points.length === 0) {
      alert('No points of interest to check. Please add some POIs first.');
      return;
    }

    const validPoints = points.filter(p => p.inDepthDescription && p.inDepthDescription.trim().length > 0);

    if (validPoints.length === 0) {
      alert('No in-depth descriptions found. Please add descriptions to your POIs first.');
      return;
    }

    setQaType('museum');
    uiState.setShowQADialog(true);
    setIsQAChecking(true);
    setQaResults({ totalPois: validPoints.length });

    try {
      const results = await qaService.detectCriticism(validPoints, selectedModel);
      setQaResults(results);
    } catch (error) {
      console.error('Museum QA check failed:', error);
      setQaResults({
        analysis: `❌ QA check failed: ${error.message}\n\nPlease try again or contact support if the issue persists.`,
        totalPois: validPoints.length
      });
    } finally {
      setIsQAChecking(false);
    }
  };

  // Handler to rewrite all descriptions
  const handleGenerateAllShortDescriptions = async () => {
    const points = itineraryState.itinerary.points || [];

    if (points.length === 0) {
      alert('No points of interest found. Please add some stops first.');
      return;
    }

    const confirmed = window.confirm(
      `This will generate short descriptions for all ${points.length} stop${points.length !== 1 ? 's' : ''}.\n\nExisting short descriptions will be overwritten.\n\nContinue?`
    );
    if (!confirmed) return;

    setIsGeneratingDescriptions(true);
    try {
      const updatedPoints = await contentRewriteService.generateAllShortDescriptions(
        points,
        itineraryState.itinerary.metadata,
        selectedModel,
        language
      );

      itineraryState.setItinerary(prev => ({ ...prev, points: updatedPoints }));
      alert(`✅ Short descriptions generated for ${updatedPoints.length} stop${updatedPoints.length !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error('Short description generation failed:', error);
      alert(`❌ Failed: ${error.message}`);
    } finally {
      setIsGeneratingDescriptions(false);
    }
  };

  const handleRewriteDescriptions = async () => {
    const points = itineraryState.itinerary.points || [];

    if (points.length === 0) {
      alert('No points of interest to rewrite. Please add some POIs first.');
      return;
    }

    const validPoints = points.filter(p => p.inDepthDescription && p.inDepthDescription.trim().length > 0);

    if (validPoints.length === 0) {
      alert('No descriptions found to rewrite. Please add descriptions to your POIs first.');
      return;
    }

    // Confirm before rewriting
    const confirmed = window.confirm(
      `⚠️ This will rewrite the in-depth descriptions of ${validPoints.length} POI${validPoints.length !== 1 ? 's' : ''}.\n\nMake sure to save your current work first. This cannot be undone easily.\n\nContinue?`
    );

    if (!confirmed) {
      return;
    }

    setIsRewriting(true);

    try {
      console.log('Starting description rewrite...');
      const rewrittenPoints = await contentRewriteService.rewriteAllDescriptions(validPoints, selectedModel, language);

      // Map rewritten points back to original itinerary
      const updatedPoints = itineraryState.itinerary.points.map(originalPoi => {
        const rewritten = rewrittenPoints.find(p => p.id === originalPoi.id);
        return rewritten || originalPoi;
      });

      // Update itinerary with rewritten descriptions
      itineraryState.setItinerary(prev => ({
        ...prev,
        points: updatedPoints
      }));

      alert(`✅ Successfully rewritten ${rewrittenPoints.length} description${rewrittenPoints.length !== 1 ? 's' : ''}!\n\nYour guide content has been enhanced and is ready for review.`);
    } catch (error) {
      console.error('Rewrite failed:', error);
      alert(`❌ Rewrite failed: ${error.message}\n\nPlease try again or contact support if the issue persists.`);
    } finally {
      setIsRewriting(false);
    }
  };

  const handleGenerateKnowledgeForAllStops = async () => {
    const editableHistorical = hasFieldAccess('historicalContext', 'edit');
    const editableCultural = hasFieldAccess('culturalSignificance', 'edit');

    if (!editableHistorical && !editableCultural) {
      setKnowledgeBatchStatus('error');
      setKnowledgeBatchMessage('You do not have permission to generate supplementary content.');
      return;
    }

    if (!Array.isArray(itineraryState.itinerary.points) || itineraryState.itinerary.points.length === 0) {
      setKnowledgeBatchStatus('error');
      setKnowledgeBatchMessage('Add at least one stop before generating supplementary content.');
      return;
    }

    setIsGeneratingKnowledgeBatch(true);
    setKnowledgeBatchStatus(null);
    setKnowledgeBatchMessage('');

    try {
      const knowledgeContext = await buildKnowledgeContext(itineraryState.itinerary.metadata, {
        maxDocs: 20,
        maxChars: 250000,
        fullDocuments: true,
        heading: 'Uploaded knowledge documents:'
      });
      const modelId = selectedModel || 'claude-sonnet-4-6';
      const outputLanguage = itineraryState.itinerary.metadata?.language || 'en-US';
      const location = [
        itineraryState.itinerary.metadata?.city,
        itineraryState.itinerary.metadata?.country
      ].filter(Boolean).join(', ');
      const stopList = itineraryState.itinerary.points.map((point, index) => {
        const stopId = String(point.id || `stop-${index + 1}`);
        return [
          `STOP_ID: ${stopId}`,
          `Stop name: ${point.name || 'Unnamed stop'}`,
          `Location: ${location || 'Unknown location'}`,
          point.description ? `Stop summary: ${point.description}` : '',
          point.category ? `Category: ${point.category}` : '',
          point.hiddenGems ? `Known details: ${point.hiddenGems}` : '',
          point.coordinates?.latitude && point.coordinates?.longitude
            ? `Coordinates: ${point.coordinates.latitude}, ${point.coordinates.longitude}`
            : ''
        ].filter(Boolean).join('\n');
      }).join('\n\n---\n\n');

      const userPrompt = [
        knowledgeContext ? `Knowledge source:\n${knowledgeContext}` : '',
        'Generate supplementary content for every stop below in one response.',
        'Return exactly this tagged structure for each stop and nothing else:',
        '[[STOP:STOP_ID]]',
        '[[HISTORICAL_CONTEXT]]',
        '- bullet 1',
        '- bullet 2',
        '- bullet 3',
        '[[/HISTORICAL_CONTEXT]]',
        '[[CULTURAL_SIGNIFICANCE]]',
        '- bullet 1',
        '- bullet 2',
        '- bullet 3',
        '[[/CULTURAL_SIGNIFICANCE]]',
        'Repeat that structure for every stop using its real STOP_ID.',
        'Each section must contain 3 or 4 short bullet points.',
        'Each bullet must be one short sentence.',
        'Use details that are specific to each stop, not generic facts about the wider area.',
        'If the source does not support a fact for a stop, leave that section empty.',
        'Use only facts supported by the knowledge source. Do not invent anything.',
        `Write in ${outputLanguage}.`,
        `Stops:\n${stopList}`
      ].filter(Boolean).join('\n\n');

      const messages = [
        {
          role: 'system',
          content: 'Return only the requested tagged sections for all stops. No JSON, no markdown fences, no explanations.'
        },
        {
          role: 'user',
          content: userPrompt
        }
      ];

      const raw = await aiService.getCompletion(messages, modelId, {
        maxTokens: 6000,
        temperature: 0.3
      });

      let parsedByStop;
      try {
        parsedByStop = parseBatchSupplementaryContentResponse(
          raw,
          itineraryState.itinerary.points.map((point, index) => String(point.id || `stop-${index + 1}`))
        );
      } catch (parseError) {
        console.error('Batch supplementary content parse failed', {
          rawResponse: raw,
          parseError: parseError.message
        });
        throw parseError;
      }

      const nextPoints = itineraryState.itinerary.points.map((point, index) => {
        const stopId = String(point.id || `stop-${index + 1}`);
        const parsed = parsedByStop[stopId] || {};

        return {
          ...point,
          historicalContext: editableHistorical
            ? (parsed.historicalContext || point.historicalContext || '')
            : (point.historicalContext || ''),
          culturalSignificance: editableCultural
            ? (parsed.culturalSignificance || point.culturalSignificance || '')
            : (point.culturalSignificance || '')
        };
      });

      itineraryState.setItinerary(prev => ({
        ...prev,
        points: nextPoints,
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      }));
      itineraryState.setHasUnsavedChanges(true);

      setKnowledgeBatchStatus('success');
      setKnowledgeBatchMessage(
        `Generated supplementary content for ${nextPoints.filter(point => point.historicalContext || point.culturalSignificance).length} stops.`
      );
    } catch (error) {
      console.error('Failed to generate knowledge-based content for all stops:', error);
      setKnowledgeBatchStatus('error');
      setKnowledgeBatchMessage(`Failed to generate supplementary content: ${error.message}`);
    } finally {
      setIsGeneratingKnowledgeBatch(false);
    }
  };

  // Handler to apply QA changes to POIs
  const handleApplyQAChanges = (selectedActions, allIssues) => {
    try {
      const updatedPoints = itineraryState.itinerary.points.map(poi => {
        let updatedPoi = { ...poi };

        // Find all accepted issues that affect this POI
        allIssues.forEach(issue => {
          const action = selectedActions[issue.id];
          if (action?.action === 'accept' && issue.affectedPois.includes(poi.name)) {
            // Update the appropriate field based on issue.field
            const fieldToUpdate = issue.field || 'inDepthDescription';

            // Do a surgical text replacement - only replace the problematic sentence/phrase
            if (updatedPoi[fieldToUpdate]) {
              const originalText = updatedPoi[fieldToUpdate];

              // Replace the currentText with suggestedText
              updatedPoi[fieldToUpdate] = originalText.replace(
                issue.currentText,
                action.text
              );

              // Log the change
              console.log(`Updated ${poi.name} - ${fieldToUpdate}`);
              console.log(`Replaced: "${issue.currentText.substring(0, 50)}..."`);
              console.log(`With: "${action.text.substring(0, 50)}..."`);
            }
          }
        });

        return updatedPoi;
      });

      // Update the itinerary with the new points
      itineraryState.setItinerary(prev => ({
        ...prev,
        points: updatedPoints
      }));

      // Count changes applied
      const changesApplied = Object.values(selectedActions).filter(a => a.action === 'accept').length;

      alert(`✅ Applied ${changesApplied} change${changesApplied !== 1 ? 's' : ''} successfully!\n\nYour guide content has been updated.`);

      // Close dialog
      uiState.setShowQADialog(false);
      setQaResults(null);
      setQaType(null);
    } catch (error) {
      console.error('Failed to apply QA changes:', error);
      alert(`❌ Failed to apply changes: ${error.message}`);
    }
  };

  // Initialize services with dependencies
  const services = useMemo(() => {
    const dependencies = {
      itineraryState,
      uiState,
      draftState,
      translationState
    };

    const fileOperations = new FileOperations(dependencies);
    const databaseOperations = new DatabaseOperations(dependencies);

    // Add file operations reference to itinerary actions
    const itineraryActions = new ItineraryActions({
      ...dependencies,
      fileOperations
    });

    return {
      itineraryActions,
      fileOperations,
      databaseOperations
    };
  }, [itineraryState, uiState, draftState, translationState]);

  useEffect(() => {
    if (!currentUser || hasHandledExternalGuideLoadRef.current) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    const loadGuideUrl = currentUrl.searchParams.get('loadGuideUrl');
    const loadGuideId = currentUrl.searchParams.get('loadGuideId');
    const requestedTab = readWorkspaceUrlState(currentUrl.toString()).tab;

    if (!loadGuideUrl) {
      hasHandledExternalGuideLoadRef.current = true;
      return;
    }

    hasHandledExternalGuideLoadRef.current = true;

    fetchGuideWithAuth(currentUser, loadGuideUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch guide: ${response.status}`);
        }

        const guideData = await response.json();
        services.itineraryActions.handleLoadGuideFromListing(
          guideData,
          loadGuideId || null,
          loadGuideUrl,
          requestedTab
        );
      })
      .catch((error) => {
        console.error('Failed to auto-load guide from URL:', error);
        replaceWorkspaceUrl({ guideUrl: null, guideId: null, tab: 'metadata' });
      });
  }, [currentUser, services]);

  useEffect(() => {
    if (uiState.currentStep !== 2) return;
    replaceWorkspaceUrl({
      section: 'builder',
      tab: uiState.activeTab,
      ...(sourceGuideUrl ? {
        guideUrl: sourceGuideUrl,
        guideId: routeMetadata.id || '',
      } : {}),
    });
  }, [routeMetadata.id, sourceGuideUrl, uiState.activeTab, uiState.currentStep]);

  // Auto-dismiss execution result toast after 5 seconds
  useEffect(() => {
    if (uiState.executionResult) {
      const timer = setTimeout(() => {
        uiState.setExecutionResult(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [uiState.executionResult, uiState]);

  // Auto-fetch _routes.json whenever a guide is loaded (metadata identity changes)
  useEffect(() => {
    if (!routeCountry || (!routeName && !routeFilename)) {
      setRouteLegs([]);
      return;
    }

    const safeCountry = routeCountry.trim().toLowerCase()
      .replace(/[^\w]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

    let folderName;
    if (routeFilename) {
      folderName = sanitizeFilename(routeFilename, { maxLength: Infinity, preserveHyphens: true }).replace(/\.json$/, '');
    } else {
      folderName = generateSafeFolderName(routeMetadata);
    }

    if (!folderName) {
      setRouteLegs([]);
      return;
    }

    const folderPath = `special_itineraries/${safeCountry}/${folderName}`;
    const expectedFilename = `${folderName}_routes.json`;
    let routesObjectPath = `${folderPath}/${expectedFilename}`;

    if (sourceGuideUrl) {
      const sourceGuidePath = extractStoragePathFromUrl(sourceGuideUrl);
      if (sourceGuidePath) {
        const sourceSegments = sourceGuidePath.split('/').filter(Boolean);
        if (sourceSegments.length >= 2) {
          const guideFilename = sourceSegments[sourceSegments.length - 1];
          const sourceFolderSegments = sourceSegments.slice(0, -1);
          const sourceFolderPath = sourceFolderSegments.join('/');
          const baseFilename = guideFilename.replace(/\.json$/, '');
          routesObjectPath = `${sourceFolderPath}/${baseFilename}_routes.json`;
        }
      }
    }

    let cancelled = false;
    const routesFetchUrl = `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/${routesObjectPath}`;
    setSavedRoutesFilePath(routesObjectPath);
    setSavedRoutesFileStatus('checking');
    setLegsLog([`Checking saved routes at "${routesObjectPath}"...`]);
    setLegsStatus(null);

    fetchGuideWithAuth(currentUser, routesFetchUrl)
      .then(res => {
        if (res.status === 404) return null;
        if (!res.ok) {
          return res.text().then((body) => {
            throw new Error(`HTTP ${res.status}${body ? `: ${body}` : ''}`);
          });
        }
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        if (data?.legs) {
          setRouteLegs(data.legs);
          setRouteMode(data.route_mode || data.legs[0]?.route_mode || 'walking');
          setSavedRoutesFileStatus('present');
          console.log(`Auto-loaded ${data.legs.length} route leg(s) from ${routesObjectPath}`);
          setLegsLog([
            `Checking saved routes at "${routesObjectPath}"...`,
            `Auto-loaded ${data.legs.length} leg(s) from "${routesObjectPath}".`
          ]);
          setLegsStatus('success');
        } else {
          setRouteLegs([]);
          setSavedRoutesFileStatus('missing');
          setLegsLog([
            `Checking saved routes at "${routesObjectPath}"...`,
            `No saved routes file found at "${routesObjectPath}".`
          ]);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('Could not load routes file:', err.message);
          setSavedRoutesFileStatus('error');
          setLegsLog([
            `Checking saved routes at "${routesObjectPath}"...`,
            `Could not load routes file: ${err.message}`
          ]);
          setLegsStatus('error');
        }
      });

    return () => { cancelled = true; };
  }, [
    routeFilename,
    routeName,
    routeCountry,
    sourceGuideUrl,
  ]);

  const handleComputeLegs = async () => {
    setIsComputingLegs(true);
    setLegsLog([]);
    setLegsStatus(null);

    const appendLog = (msg) => {
      setLegsLog((prev) => [...prev, msg]);
      setTimeout(() => legsLogEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    };
    const legs = [];

    try {
      appendLog('Starting route leg computation…');
      return await handleComputeLegsAdvanced();
      appendLog(`Done — ${legs.length} leg(s) computed.`);
    } catch (err) {
      appendLog(`Fatal error: ${err.message}`);
      setLegsStatus('error');
    } finally {
      setIsComputingLegs(false);
    }
  };

  const handleComputeLegsAdvanced = async () => {
    setIsComputingLegs(true);
    setLegsLog([]);
    setLegsStatus(null);

    const appendLog = (msg) => {
      setLegsLog((prev) => [...prev, msg]);
      setTimeout(() => legsLogEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    };

    try {
      appendLog('Starting route leg computation...');
      const computedLegs = [];

      for (const plan of routePlans) {
        const selectedMode = legRouteModes[plan.key] || plan.selectedMode || routeMode || DEFAULT_ROUTE_MODE;

        if (plan.existingLeg?.route_source === 'manual') {
          computedLegs.push({
            ...plan.existingLeg,
            route_mode: selectedMode,
          });
          appendLog(`Keeping manual leg ${plan.index + 1}/${routePlans.length}: "${plan.fromStop.name || plan.fromStop.id}" -> "${plan.toStop.name || plan.toStop.id}".`);
          continue;
        }

        appendLog(`Computing ${selectedMode} leg ${plan.index + 1}/${routePlans.length}: "${plan.fromStop.name || plan.fromStop.id}" -> "${plan.toStop.name || plan.toStop.id}"...`);
        try {
          const leg = await computeSingleRouteLeg(plan.fromStop, plan.toStop, selectedMode);
          computedLegs.push(leg);
          appendLog(`  OK ${leg.distance_meters} m, ${Math.round(leg.duration_seconds / 60)} min`);
        } catch (err) {
          appendLog(`  Error: ${err.message} - leg skipped.`);
        }
      }

      setRouteLegs([...computedLegs, ...extraRouteLegs]);
      setManualEditLegKey(null);
      setManualEditExtraLegId(null);
      setManualDraftPoints([]);
      appendLog(`Done - ${computedLegs.length} leg(s) available.`);
      setLegsStatus('success');
    } catch (err) {
      appendLog(`Fatal error: ${err.message}`);
      setLegsStatus('error');
    } finally {
      setIsComputingLegs(false);
    }
  };

  const upsertRouteLeg = (leg) => {
    const legKey = getLegStorageKey(leg);
    const nextLegs = routeLegs.filter((existingLeg) => getLegStorageKey(existingLeg) !== legKey);
    nextLegs.push(leg);
    return nextLegs;
  };

  const getStopById = (stopId) => itineraryState.itinerary.points.find((point) => point.id === stopId) || null;

  const createExtraLegDraft = () => {
    const points = itineraryState.itinerary.points || [];
    if (points.length < 2) return null;

    const fromStop = points[points.length - 1];
    const toStop = points[0];
    return {
      id: makeExtraLegId(),
      fromStop,
      toStop,
      from_stop_id: fromStop.id,
      to_stop_id: toStop.id,
      route_mode: routeMode || DEFAULT_ROUTE_MODE,
      route_source: 'manual',
      is_extra: true,
    };
  };

  const updateExtraLeg = (legId, updates) => {
    setExtraRouteLegs((prev) => prev.map((leg) => (
      leg.id === legId ? { ...leg, ...updates } : leg
    )));
    setRouteLegs((prev) => prev.map((leg) => (
      leg.id === legId ? { ...leg, ...updates } : leg
    )));
  };

  const handleExtraLegEndpointChange = (legId, field, stopId) => {
    const stop = getStopById(stopId);
    if (!stop) return;
    const currentLeg = extraRouteLegs.find((leg) => leg.id === legId);
    if (!currentLeg) return;

    const nextFromStop = field === 'from' ? stop : currentLeg.fromStop || getStopById(currentLeg.from_stop_id);
    const nextToStop = field === 'to' ? stop : currentLeg.toStop || getStopById(currentLeg.to_stop_id);
    if (!nextFromStop || !nextToStop) return;

    const rebuilt = {
      ...buildManualLeg(nextFromStop, nextToStop, [], currentLeg.route_mode || routeMode || DEFAULT_ROUTE_MODE),
      id: legId,
      is_extra: true,
      fromStop: nextFromStop,
      toStop: nextToStop,
    };

    updateExtraLeg(legId, rebuilt);
  };

  const handleLegModeChange = (legKey, nextMode) => {
    setLegRouteModes((prev) => ({ ...prev, [legKey]: nextMode }));
    setRouteLegs((prev) => prev.map((leg) => (
      makeLegKey(leg.from_stop_id, leg.to_stop_id) === legKey
        ? { ...leg, route_mode: nextMode }
        : leg
    )));
  };

  const handleExtraLegModeChange = (legId, nextMode) => {
    setExtraRouteLegs((prev) => prev.map((leg) => (
      leg.id === legId ? { ...leg, route_mode: nextMode } : leg
    )));
    setRouteLegs((prev) => prev.map((leg) => (
      leg.id === legId ? { ...leg, route_mode: nextMode } : leg
    )));
  };

  const startManualEdit = (plan) => {
    const existingPolyline = plan.existingLeg?.polyline || [];
    const initialDraft = existingPolyline.length > 2 ? existingPolyline.slice(1, -1) : [];
    setManualEditLegKey(plan.key);
    setManualEditExtraLegId(null);
    setManualDraftPoints(initialDraft);
    setLegsLog([
      `Manual edit started for "${plan.fromStop.name || plan.fromStop.id}" -> "${plan.toStop.name || plan.toStop.id}".`,
      'Click on the map to add route points or drag the numbered handles to fine-tune the path, then save the manual leg.',
    ]);
    setLegsStatus(null);
  };

  const startManualExtraLegEdit = (extraLeg) => {
    const existingPolyline = extraLeg?.polyline || [];
    const initialDraft = existingPolyline.length > 2 ? existingPolyline.slice(1, -1) : [];
    setManualEditLegKey(null);
    setManualEditExtraLegId(extraLeg.id);
    setManualDraftPoints(initialDraft);
    setLegsLog([
      `Manual edit started for extra leg "${extraLeg.fromStop.name || extraLeg.fromStop.id}" -> "${extraLeg.toStop.name || extraLeg.toStop.id}".`,
      'Click on the map to add route points or drag the numbered handles to fine-tune the path, then save the manual leg.',
    ]);
    setLegsStatus(null);
  };

  const handleManualMapClick = (point) => {
    if (!activeManualTarget) return;
    setManualDraftPoints((prev) => [...prev, point]);
  };

  const handleManualDraftPointDrag = (index, point) => {
    setManualDraftPoints((prev) => prev.map((existingPoint, pointIndex) => (
      pointIndex === index ? point : existingPoint
    )));
    setLegsLog((prev) => [
      ...prev.slice(-3),
      `Moved waypoint ${index + 1} to ${point[0].toFixed(6)}, ${point[1].toFixed(6)}.`,
    ]);
    setLegsStatus('success');
  };

  const handleManualDraftPointRemove = (index) => {
    setManualDraftPoints((prev) => prev.filter((_, pointIndex) => pointIndex !== index));
    setLegsLog((prev) => [
      ...prev.slice(-3),
      `Removed waypoint ${index + 1}.`,
    ]);
    setLegsStatus('success');
  };

  const handleManualDraftPointInsert = (index, point) => {
    setManualDraftPoints((prev) => {
      const next = [...prev];
      next.splice(index, 0, point);
      return next;
    });
    setLegsLog((prev) => [
      ...prev.slice(-3),
      `Inserted waypoint between segments ${index + 1} and ${index + 2} at ${point[0].toFixed(6)}, ${point[1].toFixed(6)}.`,
    ]);
    setLegsStatus('success');
  };

  const handleSaveManualLeg = () => {
    if (!activeManualTarget) return;

    if (activeManualPlan) {
      const selectedMode = legRouteModes[activeManualPlan.key] || activeManualPlan.selectedMode || routeMode || DEFAULT_ROUTE_MODE;
      const manualLeg = buildManualLeg(activeManualPlan.fromStop, activeManualPlan.toStop, manualDraftPoints, selectedMode);
      setRouteLegs(upsertRouteLeg(manualLeg));
      setManualEditLegKey(null);
      setManualDraftPoints([]);
      setLegsLog([
        `Saved manual leg for "${activeManualPlan.fromStop.name || activeManualPlan.fromStop.id}" -> "${activeManualPlan.toStop.name || activeManualPlan.toStop.id}".`,
      ]);
      setLegsStatus('success');
      return;
    }

    if (activeManualExtraLeg) {
      const selectedMode = activeManualExtraLeg.route_mode || routeMode || DEFAULT_ROUTE_MODE;
      const manualLeg = {
        ...buildManualLeg(activeManualExtraLeg.fromStop, activeManualExtraLeg.toStop, manualDraftPoints, selectedMode),
        id: activeManualExtraLeg.id,
        is_extra: true,
        fromStop: activeManualExtraLeg.fromStop,
        toStop: activeManualExtraLeg.toStop,
      };
      setExtraRouteLegs((prev) => {
        const next = prev.filter((leg) => leg.id !== manualLeg.id);
        next.push(manualLeg);
        return next;
      });
      setRouteLegs((prev) => {
        const next = prev.filter((leg) => leg.id !== manualLeg.id);
        next.push(manualLeg);
        return next;
      });
      setManualEditExtraLegId(null);
      setManualDraftPoints([]);
      setLegsLog([
        `Saved extra manual leg for "${activeManualExtraLeg.fromStop.name || activeManualExtraLeg.fromStop.id}" -> "${activeManualExtraLeg.toStop.name || activeManualExtraLeg.toStop.id}".`,
      ]);
      setLegsStatus('success');
      return;
    }

  };

  const handleCancelManualLeg = () => {
    setManualEditLegKey(null);
    setManualEditExtraLegId(null);
    setManualDraftPoints([]);
    setLegsLog(['Manual route editing cancelled.']);
    setLegsStatus(null);
  };

  const updateMapOverlay = (updates) => {
    itineraryState.updateMetadata({
      mapOverlay: {
        ...(itineraryState.itinerary.metadata?.mapOverlay || {}),
        ...updates,
      }
    });
    itineraryState.setHasUnsavedChanges(true);
  };

  const handleOverlayImageUploaded = (imageUrl) => {
    if (!imageUrl) {
      handleClearMapOverlay();
      return;
    }

    const probe = new window.Image();
    probe.onload = () => {
      const aspectRatio = probe.naturalWidth && probe.naturalHeight
        ? probe.naturalWidth / probe.naturalHeight
        : 1;
      updateMapOverlay({
        imageUrl,
        visible: true,
        opacity: typeof mapOverlay.opacity === 'number' ? mapOverlay.opacity : 0.72,
        rotationDeg: Number.isFinite(mapOverlay.rotationDeg) ? mapOverlay.rotationDeg : 0,
        center: mapOverlayCenter,
        widthMeters: mapOverlayWidthMeters,
        aspectRatio,
        imageWidth: probe.naturalWidth || null,
        imageHeight: probe.naturalHeight || null,
      });
      setLegsLog([`Overlay image loaded (${probe.naturalWidth}x${probe.naturalHeight}). Aspect ratio locked.`]);
      setLegsStatus('success');
    };
    probe.onerror = () => {
      updateMapOverlay({
        imageUrl,
        visible: true,
        opacity: typeof mapOverlay.opacity === 'number' ? mapOverlay.opacity : 0.72,
        rotationDeg: Number.isFinite(mapOverlay.rotationDeg) ? mapOverlay.rotationDeg : 0,
        center: mapOverlayCenter,
        widthMeters: mapOverlayWidthMeters,
        aspectRatio: mapOverlayAspectRatio,
      });
      setLegsLog(['Overlay image uploaded, but image dimensions could not be read.']);
      setLegsStatus('error');
    };
    probe.src = imageUrl;
  };

  const handleOverlayWidthChange = (nextWidthMeters) => {
    updateMapOverlay({
      widthMeters: Math.max(10, Math.round(nextWidthMeters)),
      visible: true,
    });
  };

  const handleOverlayNudge = (deltaNorthMeters, deltaEastMeters) => {
    const [currentLat, currentLng] = mapOverlayCenter;
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = 111320 * Math.cos((currentLat * Math.PI) / 180) || 1;

    updateMapOverlay({
      center: [
        currentLat + (deltaNorthMeters / metersPerDegreeLat),
        currentLng + (deltaEastMeters / metersPerDegreeLng),
      ],
      visible: true,
    });
  };

  const handleOverlayCenterToViewport = () => {
    if (!Array.isArray(mapViewport?.center) || mapViewport.center.length !== 2) {
      setLegsLog(['Move the map first so the overlay can be centered on the current view.']);
      setLegsStatus('error');
      return;
    }

    updateMapOverlay({
      center: mapViewport.center,
      visible: true,
    });
    setLegsLog(['Overlay centered on the current map view.']);
    setLegsStatus('success');
  };

  const handleOverlayMapChange = (updates) => {
    updateMapOverlay({
      ...updates,
      visible: true,
    });
  };

  const applyStopMapCoordinates = (stopId, coordinates) => {
    const [latitude, longitude] = coordinates;
    itineraryState.updatePoi(stopId, {
      coordinates: {
        latitude: String(latitude),
        longitude: String(longitude),
      }
    });
    itineraryState.setHasUnsavedChanges(true);
  };

  const getNextStopIdForPlacement = (currentStopId) => {
    const points = narratedPoints;
    const currentIndex = points.findIndex((point) => point.id === currentStopId);
    if (currentIndex === -1) return currentStopId;

    for (let i = currentIndex + 1; i < points.length; i += 1) {
      const point = points[i];
      const lat = parseFloat(point?.coordinates?.latitude);
      const lng = parseFloat(point?.coordinates?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return point.id;
      }
    }

    return points[Math.min(currentIndex + 1, points.length - 1)]?.id || currentStopId;
  };

  const handleStopPlacementMapClick = (coordinates) => {
    if (!selectedStopIdForMap) return;
    applyStopMapCoordinates(selectedStopIdForMap, coordinates);
    const currentStop = itineraryState.itinerary.points.find((point) => point.id === selectedStopIdForMap);
    const nextStopId = getNextStopIdForPlacement(selectedStopIdForMap);
    setLegsLog([`Placed "${currentStop?.name || selectedStopIdForMap}" at ${coordinates[0].toFixed(6)}, ${coordinates[1].toFixed(6)}.`]);
    setLegsStatus('success');
    if (nextStopId && nextStopId !== selectedStopIdForMap) {
      setSelectedStopIdForMap(nextStopId);
    } else if (isSequentialStopPlacementMode) {
      setIsSequentialStopPlacementMode(false);
      setIsStopPlacementMode(false);
      setLegsLog([`Placed "${currentStop?.name || selectedStopIdForMap}". All stops are now positioned.`]);
    }
  };

  const handleStopMarkerDrag = (stopId, coordinates) => {
    applyStopMapCoordinates(stopId, coordinates);
  };

  const handlePopupStopPlacementMapClick = (coordinates) => {
    if (!isBusTourGuide) return;

    const popupStopNumber = popupStopPoints.length + 1;
    const newPopupStop = {
      ...initialPoiState,
      id: generatePoiId(itineraryState.itinerary.metadata, itineraryState.itinerary.points),
      name: `Popup Stop ${popupStopNumber}`,
      pointType: 'popup_stop',
      category: 'popup_stop',
      coordinates: {
        latitude: coordinates[0].toFixed(6),
        longitude: coordinates[1].toFixed(6),
      },
      language: language || itineraryState.itinerary.metadata?.language || 'en',
      tier: 'free',
      description: '',
      thingsToSee: [],
      triggerRadiusMeters: '',
    };

    itineraryState.setItinerary((prev) => ({
      ...prev,
      points: [...(prev.points || []), newPopupStop],
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString(),
      },
    }));
    itineraryState.setCurrentPoi(newPopupStop);
    itineraryState.setIsUpdateMode(true);
    itineraryState.setOriginalPoi(JSON.parse(JSON.stringify(newPopupStop)));
    itineraryState.setHasUnsavedChanges(false);
    itineraryState.setErrors({});
    setIsPopupStopPlacementMode(false);
    setLegsStatus('success');
    setLegsLog([
      `Created "${newPopupStop.name}" at ${coordinates[0].toFixed(6)}, ${coordinates[1].toFixed(6)}. Fill in its details now.`,
    ]);
    uiState.setActiveTab('edit-poi');
  };

  const handleClearMapOverlay = () => {
    updateMapOverlay({
      imageUrl: '',
      center: null,
      widthMeters: null,
      aspectRatio: null,
      imageWidth: null,
      imageHeight: null,
      rotationDeg: 0,
      opacity: 0.72,
      visible: false,
    });
    setIsOverlayEditMode(false);
    setLegsLog(['Map overlay cleared.']);
    setLegsStatus(null);
  };

  const handleDownloadRoutes = () => {
    if (!routeLegs.length) return;
    const meta = itineraryState.itinerary.metadata;
    const safeName = (meta.name || 'guide')
      .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const routesData = {
      guide_id: meta.id || '',
      guide_name: meta.name || '',
      computed_at: new Date().toISOString(),
      route_mode: routeMode,
      legs: routeLegs,
    };
    const blob = new Blob([JSON.stringify(routesData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}_routes.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadRoutesFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data.legs)) throw new Error('No "legs" array found in file.');
        const hydratedLegs = data.legs.map((leg) => {
          if (!leg?.is_extra) return leg;
          const fromStop = getStopById(leg.from_stop_id);
          const toStop = getStopById(leg.to_stop_id);
          return {
            ...leg,
            fromStop: leg.fromStop || fromStop || null,
            toStop: leg.toStop || toStop || null,
          };
        });
        setRouteLegs(hydratedLegs);
        setExtraRouteLegs(hydratedLegs.filter((leg) => leg?.is_extra));
        setRouteMode(data.route_mode || data.legs[0]?.route_mode || 'walking');
        setLegsStatus('success');
        setLegsLog([`Loaded ${data.legs.length} leg(s) from "${file.name}".`]);
      } catch (err) {
        setLegsLog([`Failed to load routes file: ${err.message}`]);
        setLegsStatus('error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Render different tabs based on activeTab
  const renderTabContent = () => {
    const { activeTab } = uiState;
    const { itinerary, currentPoi, hasUnsavedChanges, isUpdateMode } = itineraryState;

    switch (activeTab) {
      case 'venue-page':
        return (
          <VenueLandingPageSection
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
          />
        );

      case 'museum-info':
        return (
          <MuseumInfoSection
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
          />
        );

      case 'metadata':
        return (
          <MetadataSection
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
            b2bClient={itineraryState.b2bClient}
            setB2bClient={itineraryState.setB2bClient}
            selectedModel={selectedModel}
            showKnowledgeTools={false}
            errors={itineraryState.errors}
          />
        );

      case 'rag':
        return (
          <RagAndBlocksTab
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
            onStatusChange={(status, message) => {
              console.log(`RAG Status: ${status} - ${message}`);
            }}
          />
        );

      case 'story-ai':
        return (
          <StoryAITab
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
          />
        );

      case 'ai-guide-content':
        return (
          <AiGuideContentTab
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
            onPublishAiConfigToLinkedLanguages={publishAiConfigToLinkedLanguages}
          />
        );

      case 'media-package':
        return (
          <MediaPackageTab
            itinerary={itinerary}
            setItinerary={itineraryState.setItinerary}
          />
        );

      case 'quality-review':
        return (
          <GuideQualityEvaluator
            itinerary={itinerary}
            selectedModel={selectedModel}
            onModelChange={changeModel}
            evaluation={qualityEvaluation}
            onEvaluationChange={setQualityEvaluation}
          />
        );

      case 'pois':
        return (
          <div className="space-y-8">
            {/* Stops list (primary content) */}
            <div className="overflow-y-auto bg-gray-50">
              <POIList
                points={itineraryState.itinerary.points}
                onEdit={services.itineraryActions.handleEditPoi}
                onRemove={services.itineraryActions.handleRemovePoi}
                onReorder={services.itineraryActions.handleReorderPOIs}
                isTranslatingAll={translationState.isTranslatingAll}
                translatingFields={translationState.translatingFields}
                checkPointIdFormat={itineraryState.checkPointIdFormat}
                regeneratePointIds={itineraryState.regeneratePointIds}
                itineraryMetadata={itineraryState.itinerary.metadata}
              />

              <div className="mt-8 flex gap-4 justify-center">
                <button
                  onClick={services.itineraryActions.handleNewPoi}
                  className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add New Point of Interest
                </button>

                <button
                  onClick={handleBatchFieldGeneration}
                  disabled={isGenerating}
                  className="bg-purple-500 text-white px-6 py-3 rounded-lg hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  title="Generate all content fields for all POIs"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  {isGenerating ? 'Generating...' : 'Batch Generate All Fields'}
                </button>
              </div>
            </div>

            {/* Collapsible QA Section */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-300">
              {/* QA Header - Collapsible */}
              <button
                onClick={() => setQaExpanded(!qaExpanded)}
                className="w-full flex items-center justify-between p-6 hover:bg-opacity-75 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-800">QA & Rewriting</h2>
                  <span className="text-sm text-gray-500">(AI-powered content improvement)</span>
                </div>
                {qaExpanded ? (
                  <ChevronUp className="w-6 h-6 text-gray-600" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-600" />
                )}
              </button>

              {/* QA Content - Collapsible */}
              {qaExpanded && (
                <div className="border-t border-indigo-200 px-6 pb-6 space-y-6">
                  {/* Quality Assurance Checks Subsection */}
                  <div className="pt-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">
                      Quality Assurance Checks
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Use AI to analyze your guide content for consistency, quality, and appropriateness
                    </p>

                    {/* Model Selector for QA Checks */}
                    <div className="mb-4 max-w-xs">
                      <ModelSelector
                        selectedModel={selectedModel}
                        onModelChange={changeModel}
                      />
                    </div>

                    {/* QA Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleGuideQA}
                        disabled={isQAChecking || itineraryState.itinerary.points.length === 0}
                        className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600
                                   disabled:bg-gray-400 disabled:cursor-not-allowed
                                   transition-colors flex items-center gap-2"
                      >
                        {isQAChecking && qaType === 'guide' ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <CheckCircle className="w-5 h-5" />
                        )}
                        QA Check for Guides
                      </button>

                      <button
                        onClick={handleMuseumQA}
                        disabled={isQAChecking || itineraryState.itinerary.points.length === 0}
                        className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600
                                   disabled:bg-gray-400 disabled:cursor-not-allowed
                                   transition-colors flex items-center gap-2"
                      >
                        {isQAChecking && qaType === 'museum' ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <AlertTriangle className="w-5 h-5" />
                        )}
                        QA Check for Museums
                      </button>
                    </div>
                  </div>

                  {/* Rewrite Descriptions Subsection */}
                  <div className="border-t border-indigo-200 pt-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">
                      Rewrite & Enhance Descriptions
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Use AI to rewrite and enhance all descriptions for a more engaging and natural narration
                    </p>

                    {/* Model Selector for Rewrite */}
                    <div className="mb-4 max-w-xs">
                      <ModelSelector
                        selectedModel={selectedModel}
                        onModelChange={changeModel}
                      />
                    </div>

                    {/* Batch Buttons */}
                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={handleGenerateAllShortDescriptions}
                        disabled={isGeneratingDescriptions || itineraryState.itinerary.points.length === 0}
                        className="px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600
                                   disabled:bg-gray-400 disabled:cursor-not-allowed
                                   transition-colors flex items-center gap-2"
                      >
                        {isGeneratingDescriptions ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                            </svg>
                            Generate All Short Descriptions
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRewriteDescriptions}
                        disabled={isRewriting || itineraryState.itinerary.points.length === 0}
                        className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600
                                   disabled:bg-gray-400 disabled:cursor-not-allowed
                                   transition-colors flex items-center gap-2"
                      >
                        {isRewriting ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Rewriting...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Rewrite All Descriptions
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 self-center">
                        ⚠️ This will replace all descriptions. Save first!
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-indigo-200 pt-6 space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-gray-800">
                        Knowledge Documents & Supplementary Content
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload source documents here, then fill historical context and cultural significance for all stops.
                      </p>
                    </div>

                    <KnowledgeDocPanel
                      itinerary={itineraryState.itinerary}
                      setItinerary={itineraryState.setItinerary}
                    />

                    {(hasFieldAccess('historicalContext', 'edit') || hasFieldAccess('culturalSignificance', 'edit')) && (
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                        <div>
                          <p className="text-sm font-medium text-indigo-900">Batch supplementary generation</p>
                          <p className="text-xs text-indigo-700">
                            Fill historical context and cultural significance for every stop from the uploaded knowledge documents.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleGenerateKnowledgeForAllStops}
                          disabled={isGeneratingKnowledgeBatch}
                          className="flex items-center gap-2 px-3 py-2 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          {isGeneratingKnowledgeBatch ? (
                            <>
                              <Loader className="w-3 h-3 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            'Generate for all stops'
                          )}
                        </button>
                      </div>
                    )}

                    {knowledgeBatchMessage && (
                      <div className={`rounded-lg border px-3 py-2 text-sm ${
                        knowledgeBatchStatus === 'error'
                          ? 'border-red-200 bg-red-50 text-red-700'
                          : 'border-green-200 bg-green-50 text-green-700'
                      }`}>
                        {knowledgeBatchMessage}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'edit-poi':
        return (
          <EnhancedPOIForm
            poi={currentPoi}
            onPoiChange={services.itineraryActions.handlePoiChange}
            onGenerateField={handleGenerateField}
            onGenerateAudio={handleGeneratePoiAudio}
            isGenerating={isGenerating}
            onSubmit={isUpdateMode ? services.itineraryActions.handleUpdatePoi : services.itineraryActions.handleAddPoi}
            onCancel={() => {
              if (shouldWarnOnPoiExit) {
                const confirmed = window.confirm(
                  `You have unsaved changes to "${currentPoi?.name || 'this point of interest'}". Are you sure you want to discard them?`
                );
                if (!confirmed) return;
              }
              uiState.setActiveTab('pois');
            }}
            isUpdateMode={isUpdateMode}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedModel={selectedModel}
            onModelChange={changeModel}
            language={language}
            categories={POI_CATEGORIES}
            itineraryMetadata={itineraryState.itinerary.metadata}
            itineraryPoints={itineraryState.itinerary.points}
          />
        );

      case 'map':
        return (
          <div className="flex flex-col gap-4">
            <ItineraryMapView
              points={itineraryState.itinerary.points}
              itineraryMetadata={itineraryState.itinerary.metadata}
              legs={routeLegs}
              mapOverlay={mapOverlay}
              onMapViewChange={setMapViewport}
              overlayEditor={isOverlayEditMode ? {
                isActive: true,
                onChange: handleOverlayMapChange,
              } : null}
              stopPlacementEditor={isStopPlacementMode ? {
                isActive: true,
                selectedStopId: selectedStopIdForMap,
                onMapClick: handleStopPlacementMapClick,
                onMarkerDrag: handleStopMarkerDrag,
              } : null}
              popupStopEditor={isPopupStopPlacementMode ? {
                isActive: true,
                onMapClick: handlePopupStopPlacementMapClick,
              } : null}
              manualEditor={manualEditLegKey ? {
                isActive: true,
                renderDraftOverlay: true,
                draftPoints: manualDraftPoints,
                draftPolyline: activeManualDraftPolyline,
                onMapClick: handleManualMapClick,
                onSaveManualLeg: handleSaveManualLeg,
                onDraftPointInsert: handleManualDraftPointInsert,
                onDraftPointDrag: handleManualDraftPointDrag,
                onDraftPointRemove: handleManualDraftPointRemove,
              } : manualEditExtraLegId ? {
                isActive: true,
                renderDraftOverlay: true,
                draftPoints: manualDraftPoints,
                draftPolyline: activeManualDraftPolyline,
                onMapClick: handleManualMapClick,
                onSaveManualLeg: handleSaveManualLeg,
                onDraftPointInsert: handleManualDraftPointInsert,
                onDraftPointDrag: handleManualDraftPointDrag,
                onDraftPointRemove: handleManualDraftPointRemove,
              } : null}
            />

            <div className="order-1 bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Place Stops On Map</h3>
                  <p className="text-xs text-gray-500">
                    Select a stop, click on the map to place it, then drag its marker if you need to fine-tune the position.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {isBusTourGuide && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsPopupStopPlacementMode((prev) => !prev);
                        setIsStopPlacementMode(false);
                        setIsSequentialStopPlacementMode(false);
                      }}
                      className={`px-3 py-2 rounded text-sm border ${
                        isPopupStopPlacementMode
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      {isPopupStopPlacementMode ? 'Stop Adding Popup Stops' : 'Add Popup Stop'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setIsSequentialStopPlacementMode((prev) => !prev);
                      setIsPopupStopPlacementMode(false);
                      if (isSequentialStopPlacementMode) {
                        setIsStopPlacementMode(false);
                      }
                    }}
                    className={`px-3 py-2 rounded text-sm border ${
                      isSequentialStopPlacementMode
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    {isSequentialStopPlacementMode ? 'Stop Place Next' : 'Place Next Stop'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsStopPlacementMode((prev) => !prev);
                      setIsSequentialStopPlacementMode(false);
                      setIsPopupStopPlacementMode(false);
                    }}
                    className={`px-3 py-2 rounded text-sm border ${
                      isStopPlacementMode
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    {isStopPlacementMode ? 'Stop Placing' : 'Place Stops'}
                  </button>
                </div>
              </div>

              {isStopPlacementMode && (
                <div className="grid gap-3 lg:grid-cols-[280px_minmax(0,1fr)]">
                  <div>
                    <label htmlFor="map-stop-select" className="block text-sm font-medium text-gray-700 mb-1">Selected stop</label>
                    <select
                      id="map-stop-select"
                      value={selectedStopIdForMap || ''}
                      onChange={(e) => setSelectedStopIdForMap(e.target.value)}
                      className="w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700"
                    >
                      {narratedPoints.map((point, index) => {
                        const lat = parseFloat(point?.coordinates?.latitude);
                        const lng = parseFloat(point?.coordinates?.longitude);
                        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
                        return (
                          <option key={point.id || index} value={point.id}>
                            {index + 1}. {point.name || point.id} {hasCoords ? '• placed' : '• missing'}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    {selectedStopForMap
                      ? isSequentialStopPlacementMode
                        ? `Sequential mode active. Click the map to place "${selectedStopForMap.name || selectedStopForMap.id}", then it will advance automatically to the next stop.`
                        : `Current stop: ${selectedStopForMap.name || selectedStopForMap.id}. Click on the map to set coordinates. After placing, the selector advances automatically.`
                      : 'Choose a stop to begin placing it on the map.'}
                  </div>
                </div>
              )}

              {isPopupStopPlacementMode && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Click anywhere on the map to create a new popup stop. It will be added to the guide immediately and the editor will open so you can fill in its details.
                </div>
              )}
            </div>

            <div className="order-3 bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <button
                type="button"
                onClick={() => setIsMapOverlayBuilderExpanded((prev) => !prev)}
                className="flex w-full items-start justify-between gap-3 text-left"
              >
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Map Overlay Builder</h3>
                  <p className="text-xs text-gray-500">
                    Use a JPG or PNG on top of OpenStreetMap to align orientation and scale while placing stops and drawing paths.
                  </p>
                </div>
                {isMapOverlayBuilderExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-500 mt-0.5" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500 mt-0.5" />
                )}
              </button>

              {isMapOverlayBuilderExpanded && (
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
                <div>
                  <ImageUploader
                    imageUrl={mapOverlay.imageUrl || ''}
                    onImageUploaded={handleOverlayImageUploaded}
                    itineraryMetadata={itineraryState.itinerary.metadata}
                    poiName="map_overlay"
                    label="Overlay image"
                    previewSize="h-56"
                    accept="image/png,image/jpeg,image/webp"
                    maxSizeKB={400}
                  />
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-gray-700">Overlay visible</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setIsOverlayEditMode((prev) => !prev)}
                          disabled={!mapOverlay.imageUrl}
                          className={`px-3 py-1.5 rounded text-sm border ${
                            isOverlayEditMode
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-white text-gray-700 border-gray-300'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {isOverlayEditMode ? 'Stop Map Edit' : 'Edit On Map'}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateMapOverlay({ visible: !mapOverlay.visible })}
                          disabled={!mapOverlay.imageUrl}
                          className={`px-3 py-1.5 rounded text-sm border ${
                            mapOverlay.visible
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {mapOverlay.visible ? 'Visible' : 'Hidden'}
                        </button>
                      </div>
                    </div>
                    {isOverlayEditMode && mapOverlay.imageUrl && (
                      <p className="mt-2 text-xs text-amber-700">
                        Drag the center handle to move the image. Drag the side handle to resize it without distortion.
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                    <div>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <label htmlFor="overlay-opacity" className="text-sm font-medium text-gray-700">Opacity</label>
                        <span className="text-xs text-gray-500">{Math.round((typeof mapOverlay.opacity === 'number' ? mapOverlay.opacity : 0.72) * 100)}%</span>
                      </div>
                      <input
                        id="overlay-opacity"
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.05"
                        value={typeof mapOverlay.opacity === 'number' ? mapOverlay.opacity : 0.72}
                        onChange={(e) => updateMapOverlay({ opacity: parseFloat(e.target.value) })}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <label htmlFor="overlay-rotation" className="text-sm font-medium text-gray-700">Rotation</label>
                        <span className="text-xs text-gray-500">{Number.isFinite(mapOverlay.rotationDeg) ? mapOverlay.rotationDeg : 0}°</span>
                      </div>
                      <input
                        id="overlay-rotation"
                        type="range"
                        min="-180"
                        max="180"
                        step="1"
                        value={Number.isFinite(mapOverlay.rotationDeg) ? mapOverlay.rotationDeg : 0}
                        onChange={(e) => updateMapOverlay({ rotationDeg: parseInt(e.target.value, 10) || 0 })}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <label htmlFor="overlay-scale" className="text-sm font-medium text-gray-700">Width</label>
                        <span className="text-xs text-gray-500">{Math.round(mapOverlayWidthMeters)} m</span>
                      </div>
                      <input
                        id="overlay-scale"
                        type="range"
                        min="20"
                        max="5000"
                        step="10"
                        value={mapOverlayWidthMeters}
                        onChange={(e) => handleOverlayWidthChange(parseFloat(e.target.value))}
                        disabled={!mapOverlay.imageUrl}
                        className="w-full disabled:opacity-50"
                      />
                      <input
                        type="number"
                        min="10"
                        step="10"
                        value={Math.round(mapOverlayWidthMeters)}
                        onChange={(e) => handleOverlayWidthChange(parseFloat(e.target.value) || 10)}
                        disabled={!mapOverlay.imageUrl}
                        className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-sm text-gray-700 disabled:opacity-50"
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                    <div>
                      <div className="text-sm font-medium text-gray-700">Position</div>
                      <p className="text-xs text-gray-500 mt-1">
                        The image keeps its original aspect ratio. Move and scale it explicitly instead of stretching it to the viewport.
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 max-w-[220px]">
                      <div />
                      <button
                        type="button"
                        onClick={() => handleOverlayNudge(overlayNudgeMeters, 0)}
                        disabled={!mapOverlay.imageUrl}
                        className="px-3 py-2 rounded bg-white text-gray-700 hover:bg-gray-50 text-sm border border-gray-300 disabled:opacity-50"
                      >
                        Up
                      </button>
                      <div />
                      <button
                        type="button"
                        onClick={() => handleOverlayNudge(0, -overlayNudgeMeters)}
                        disabled={!mapOverlay.imageUrl}
                        className="px-3 py-2 rounded bg-white text-gray-700 hover:bg-gray-50 text-sm border border-gray-300 disabled:opacity-50"
                      >
                        Left
                      </button>
                      <button
                        type="button"
                        onClick={handleOverlayCenterToViewport}
                        disabled={!mapOverlay.imageUrl}
                        className="px-3 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 text-sm disabled:opacity-50"
                      >
                        Center
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOverlayNudge(0, overlayNudgeMeters)}
                        disabled={!mapOverlay.imageUrl}
                        className="px-3 py-2 rounded bg-white text-gray-700 hover:bg-gray-50 text-sm border border-gray-300 disabled:opacity-50"
                      >
                        Right
                      </button>
                      <div />
                      <button
                        type="button"
                        onClick={() => handleOverlayNudge(-overlayNudgeMeters, 0)}
                        disabled={!mapOverlay.imageUrl}
                        className="px-3 py-2 rounded bg-white text-gray-700 hover:bg-gray-50 text-sm border border-gray-300 disabled:opacity-50"
                      >
                        Down
                      </button>
                      <div />
                    </div>
                    <div>
                      <label htmlFor="overlay-nudge" className="text-sm font-medium text-gray-700">Nudge step</label>
                      <select
                        id="overlay-nudge"
                        value={overlayNudgeMeters}
                        onChange={(e) => setOverlayNudgeMeters(parseInt(e.target.value, 10))}
                        className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
                      >
                        <option value={5}>5 m</option>
                        <option value={10}>10 m</option>
                        <option value={25}>25 m</option>
                        <option value={50}>50 m</option>
                        <option value={100}>100 m</option>
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleClearMapOverlay}
                        className="px-3 py-2 rounded bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 text-sm border border-gray-300"
                      >
                        Clear Overlay
                      </button>
                    </div>
                    <div className="text-[11px] text-gray-500 bg-gray-50 rounded p-2">
                      {mapOverlay.imageUrl
                        ? `Center: ${mapOverlayCenter[0].toFixed(5)}, ${mapOverlayCenter[1].toFixed(5)} · Width: ${Math.round(mapOverlayWidthMeters)} m · Ratio: ${mapOverlayAspectRatio.toFixed(3)}`
                        : 'No overlay image loaded yet.'}
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>

            <div className="order-2 bg-white rounded-lg border border-gray-200 p-4">
              <button
                type="button"
                onClick={() => setIsRouteLegsExpanded((prev) => !prev)}
                className="flex w-full items-start justify-between gap-3 text-left"
              >
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Route Legs</h3>
                  <p className="text-xs text-gray-500">
                    Routes are saved as a separate <code>_routes.json</code> file — language-independent and shared across all translations of this guide.
                    {routeLegs.length > 0 && (
                      <span className="ml-1 text-purple-600 font-medium">{routeLegs.length} leg{routeLegs.length !== 1 ? 's' : ''} loaded.</span>
                    )}
                  </p>
                </div>
                {isRouteLegsExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-500 mt-0.5" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500 mt-0.5" />
                )}
              </button>

              {isRouteLegsExpanded && (
              <>
              <div className="mb-3 mt-3 flex items-center gap-2">
                <label htmlFor="route-mode" className="text-xs font-medium text-gray-600">Mode</label>
                <select
                  id="route-mode"
                  value={routeMode}
                  onChange={(e) => setRouteMode(e.target.value)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
                >
                  {ROUTE_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">Default for new legs</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* 1. Compute */}
                <button
                  onClick={handleComputeLegsAdvanced}
                  disabled={isComputingLegs || itineraryState.itinerary.points.length < 2 || !itineraryState.itinerary.metadata.country || !itineraryState.itinerary.metadata.name}
                  className="px-4 py-2 rounded flex items-center gap-2 bg-purple-500 text-white hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                >
                  {isComputingLegs ? <Loader className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  {isComputingLegs ? 'Computing…' : 'Compute Routes'}
                </button>

                {/* 2. Download (only when legs are loaded) */}
                {routeLegs.length > 0 && (
                  <button
                    onClick={handleDownloadRoutes}
                    className="px-4 py-2 rounded flex items-center gap-2 bg-purple-50 text-purple-700 hover:bg-purple-100 text-sm border border-purple-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Download Routes
                  </button>
                )}

                {/* 3. Save to Firebase (only when legs are loaded) */}
                {routeLegs.length > 0 && (
                  <button
                    onClick={async () => {
                      setLegsLog([]);
                      setLegsStatus(null);
                      const appendLog = (msg) => {
                        setLegsLog((prev) => [...prev, msg]);
                        setTimeout(() => legsLogEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                      };
                      const meta = itineraryState.itinerary.metadata;
                      const safeName = (meta.name || 'guide')
                        .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                      const routesData = {
                        guide_id: meta.id || '',
                        guide_name: meta.name || '',
                        computed_at: new Date().toISOString(),
                        route_mode: routeMode,
                        legs: routeLegs,
                      };
                      appendLog('Uploading routes to Firebase Storage…');
                      try {
                        const result = await saveRoutesToStorage(routesData, meta);
                        appendLog(`✓ Saved to Firebase: ${result.filename || `${safeName}_routes.json`}`);
                        setSavedRoutesFileStatus('present');
                        setLegsStatus('success');
                      } catch (err) {
                        appendLog(`✗ Upload failed: ${err.message}`);
                        setLegsStatus('error');
                      }
                    }}
                    disabled={!itineraryState.itinerary.metadata.country || !itineraryState.itinerary.metadata.name}
                    className="px-4 py-2 rounded flex items-center gap-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm border border-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Save to Firebase
                  </button>
                )}

                {itineraryState.itinerary.points.length > 1 && (
                  <button
                    onClick={() => {
                      const draft = createExtraLegDraft();
                      if (!draft) return;
                      setExtraRouteLegs((prev) => [...prev, draft]);
                      setRouteLegs((prev) => [...prev, draft]);
                      startManualExtraLegEdit(draft);
                    }}
                    className="px-4 py-2 rounded flex items-center gap-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm border border-emerald-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Extra Leg
                  </button>
                )}

                {/* 4. Load from file */}
                <label className="px-4 py-2 rounded flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer text-sm border border-gray-300">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Load Routes File
                  <input type="file" accept=".json" onChange={handleLoadRoutesFile} className="hidden" />
                </label>

                {/* 5. Clear */}
                {routeLegs.length > 0 && (
                  <button
                    onClick={() => { setRouteLegs([]); setExtraRouteLegs([]); setLegsLog([]); setLegsStatus(null); }}
                    className="px-4 py-2 rounded flex items-center gap-2 bg-white text-gray-500 hover:text-red-600 hover:bg-red-50 text-sm border border-gray-300"
                  >
                    Clear Routes
                  </button>
                )}
              </div>

              {routePlans.length > 0 && (
                <div className="mt-4 border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setIsPerLegRouteControlsExpanded((prev) => !prev)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 bg-gray-50 border-b text-left"
                  >
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Per-Leg Route Controls
                      </div>
                      <div className="mt-1 text-xs text-gray-500 normal-case tracking-normal">
                        Each row is the route segment between two consecutive stops. You can choose a transport mode for that segment or override it with a manual path.
                      </div>
                    </div>
                    {isPerLegRouteControlsExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                  {isPerLegRouteControlsExpanded && (
                  <div className="divide-y">
                    {routePlans.map((plan) => (
                      <div key={plan.key} className="p-3">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800">
                              Leg {plan.index + 1}: {plan.fromStop.name || plan.fromStop.id} to {plan.toStop.name || plan.toStop.id}
                            </div>
                            <div className="text-xs text-gray-500">
                              {plan.existingLeg?.route_source === 'manual'
                                ? 'Manual path preserved on recompute'
                                : plan.existingLeg
                                  ? 'Auto-computed leg'
                                  : 'No saved leg yet'}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={legRouteModes[plan.key] || plan.selectedMode}
                              onChange={(e) => handleLegModeChange(plan.key, e.target.value)}
                              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
                            >
                              {ROUTE_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>

                            <button
                              onClick={() => startManualEdit(plan)}
                              className={`rounded border px-3 py-1 text-xs ${
                                manualEditLegKey === plan.key
                                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {manualEditLegKey === plan.key ? 'Editing Manual Path' : (plan.existingLeg?.route_source === 'manual' ? 'Edit Manual Path' : 'Draw Manual Path')}
                            </button>
                          </div>
                        </div>

                        {manualEditLegKey === plan.key && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              onClick={() => setManualDraftPoints((prev) => prev.slice(0, -1))}
                              disabled={manualDraftPoints.length === 0}
                              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 disabled:opacity-50"
                            >
                              Undo Point
                            </button>
                            <button
                              onClick={() => setManualDraftPoints([])}
                              disabled={manualDraftPoints.length === 0}
                              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 disabled:opacity-50"
                            >
                              Clear Points
                            </button>
                            <button
                              onClick={handleSaveManualLeg}
                              className="rounded border border-blue-700 bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                            >
                              Save Manual Leg
                            </button>
                            <button
                              onClick={handleCancelManualLeg}
                              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700"
                            >
                              Cancel
                            </button>
                            <span className="self-center text-xs text-gray-500">
                              {manualDraftPoints.length} waypoint{manualDraftPoints.length !== 1 ? 's' : ''} added
                            </span>
                            <span className="self-center text-xs text-gray-500">
                              Drag the numbered points on the map to adjust the route.
                            </span>
                            <span className="self-center text-xs text-gray-500">
                              Click a plus handle on the line to insert a waypoint between two points.
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              )}

              {extraLegRows.length > 0 && (
                <div className="mt-4 border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-emerald-50 border-b text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                    Extra Route Legs
                  </div>
                  <div className="px-3 py-2 text-xs text-emerald-700/80 border-b bg-white">
                    These legs are added manually and do not have to follow the normal stop order. Use them for loop routes, return segments, or detours.
                  </div>
                  <div className="divide-y">
                    {extraLegRows.map((leg, index) => (
                      <div key={leg.id} className="p-3 space-y-3">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800">
                              Extra Leg {index + 1}
                            </div>
                            <div className="text-xs text-gray-500">
                              {leg.fromStop?.name || leg.from_stop_id} to {leg.toStop?.name || leg.to_stop_id}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={leg.from_stop_id}
                              onChange={(e) => handleExtraLegEndpointChange(leg.id, 'from', e.target.value)}
                              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
                              disabled={manualEditExtraLegId === leg.id}
                            >
                              {itineraryState.itinerary.points.map((point) => (
                                <option key={point.id} value={point.id}>{point.name || point.id}</option>
                              ))}
                            </select>
                            <span className="text-xs text-gray-400">to</span>
                            <select
                              value={leg.to_stop_id}
                              onChange={(e) => handleExtraLegEndpointChange(leg.id, 'to', e.target.value)}
                              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
                              disabled={manualEditExtraLegId === leg.id}
                            >
                              {itineraryState.itinerary.points.map((point) => (
                                <option key={point.id} value={point.id}>{point.name || point.id}</option>
                              ))}
                            </select>
                            <select
                              value={leg.route_mode || routeMode}
                              onChange={(e) => handleExtraLegModeChange(leg.id, e.target.value)}
                              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
                            >
                              {ROUTE_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => startManualExtraLegEdit(leg)}
                              className={`rounded border px-3 py-1 text-xs ${
                                manualEditExtraLegId === leg.id
                                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {manualEditExtraLegId === leg.id ? 'Editing Manual Path' : (leg.polyline?.length ? 'Edit Manual Path' : 'Draw Manual Path')}
                            </button>
                            <button
                              onClick={() => {
                                setExtraRouteLegs((prev) => prev.filter((item) => item.id !== leg.id));
                                setRouteLegs((prev) => prev.filter((item) => item.id !== leg.id));
                                if (manualEditExtraLegId === leg.id) {
                                  handleCancelManualLeg();
                                }
                              }}
                              className="rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {manualEditExtraLegId === leg.id && (
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => setManualDraftPoints((prev) => prev.slice(0, -1))}
                              disabled={manualDraftPoints.length === 0}
                              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 disabled:opacity-50"
                            >
                              Undo Point
                            </button>
                            <button
                              onClick={() => setManualDraftPoints([])}
                              disabled={manualDraftPoints.length === 0}
                              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 disabled:opacity-50"
                            >
                              Clear Points
                            </button>
                            <button
                              onClick={handleSaveManualLeg}
                              className="rounded border border-blue-700 bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                            >
                              Save Manual Leg
                            </button>
                            <button
                              onClick={handleCancelManualLeg}
                              className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700"
                            >
                              Cancel
                            </button>
                            <span className="self-center text-xs text-gray-500">
                              {manualDraftPoints.length} waypoint{manualDraftPoints.length !== 1 ? 's' : ''} added
                            </span>
                            <span className="self-center text-xs text-gray-500">
                              Drag the numbered points on the map to adjust the route.
                            </span>
                            <span className="self-center text-xs text-gray-500">
                              Click a plus handle on the line to insert a waypoint between two points.
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {legsLog.length > 0 && (
                <div className="mt-3 border rounded overflow-hidden">
                  <div className="bg-gray-900 text-gray-100 p-3 max-h-48 overflow-y-auto text-xs font-mono">
                    {legsLog.map((line, i) => (
                      <div key={i} className="leading-5 whitespace-pre-wrap">{line}</div>
                    ))}
                    <div ref={legsLogEndRef} />
                  </div>
                  {legsStatus === 'success' && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 text-xs border-t">
                      <Check className="w-3 h-3" /> Done.
                    </div>
                  )}
                  {legsStatus === 'error' && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs border-t">
                      <AlertCircle className="w-3 h-3" /> Error — see log above.
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Batch Audio Generation</h3>
            <BatchAudioGenerator
              itinerary={itineraryState.itinerary}
              onBatchComplete={handleBatchAudioComplete}
              onIndividualComplete={handleIndividualAudioComplete}
            />
          </div>
        );

      case 'translation':
        return (
          <TranslationModule
            itinerary={itineraryState.itinerary}
            setItinerary={itineraryState.setItinerary}
            targetLanguage={translationState.targetLanguage}
            setTargetLanguage={translationState.setTargetLanguage}
            translationModel={translationState.translationModel}
            setTranslationModel={translationState.setTranslationModel}
            isTranslatingAll={translationState.isTranslatingAll}
            translatingFields={translationState.translatingFields}
            translationProgress={translationState.translationProgress}
            setIsTranslatingAll={translationState.setIsTranslatingAll}
            setTranslatingFields={translationState.setTranslatingFields}
            onTranslateAll={handleTranslateAll}
            onTranslateField={handleTranslateField}
            onTranslatePoiDetails={handleTranslatePoiDetails}
            onTranslateAllPoiDetails={handleTranslateAllPoiDetails}
            onDownloadTranslation={handleDownloadTranslation}
            linkedLanguageProps={{
              linkedGuides: linkedGuideItems,
              selectedGuideIds: selectedLinkedGuideIds,
              loading: isLoadingLinkedGuides,
              saving: isSavingLinkedGuides,
              error: linkedGuidesError,
              onRefresh: () => loadLinkedGuides(true),
              onToggleGuide: toggleLinkedGuideSelection,
              onSelectAll: setAllLinkedGuideSelections,
              onSyncSelected: syncSelectedLinkedGuides,
              onSaveSelected: saveSelectedLinkedGuides,
              onMarkTranslationReviewed: markSelectedLinkedGuidesTranslationReviewed
            }}
          />
        );

      case 'ai-tools':
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">AI Tools</h2>
            <ContextSection
              contextFiles={itineraryState.contextFiles}
              setContextFiles={itineraryState.setContextFiles}
              contextText={itineraryState.contextText}
              setContextText={itineraryState.setContextText}
              onGenerateContent={generateContent}
              selectedModel={selectedModel}
            />
          </div>
        );


      case 'tour':
        return (
          <TourModule
            planningData={draftState.planningData}
            onPlanningChange={draftState.updatePlanningData}
            isTourGenerating={draftState.isTourGeneratingLocal}
            onGenerateTourPlan={generateTourPlan}
            itinerary={itinerary}
          />
        );

      case 'drafts':
        return (
          <DraftManager
            currentDraftId={draftState.currentDraftId}
            onLoadDraft={() => {}}
            onDeleteDraft={() => {}}
          />
        );

      case 'objects':
        return (
          <ObjectsManager />
        );

      case 'room-recognition':
        return (
          <RoomRecognitionManager
            guideId={itinerary.metadata?.id || itinerary.id || ''}
            guideName={itinerary.metadata?.name || 'Untitled Guide'}
            stops={itinerary.points || []}
            isEnabled={itinerary.metadata?.roomRecognitionEnabled || false}
            onToggleEnabled={(enabled) => {
              itineraryState.setItinerary(prev => ({
                ...prev,
                metadata: {
                  ...prev.metadata,
                  roomRecognitionEnabled: enabled,
                  has_qr_codes: enabled ? false : prev.metadata.has_qr_codes  // Room Recognition and QR codes are mutually exclusive
                }
              }));
            }}
          />
        );

      case 'vpr':
        return (
          <VprManager
            familyId={itinerary.metadata?.parent_guide_id || itinerary.metadata?.id || itinerary.metadata?.ID || itinerary.id || ''}
            guideId={itinerary.metadata?.id || itinerary.id || ''}
            guideName={itinerary.metadata?.name || 'Untitled Guide'}
            stops={itinerary.points || []}
            isEnabled={itinerary.metadata?.vprEnabled || false}
            onToggleEnabled={(enabled) => {
              itineraryState.setItinerary(prev => ({
                ...prev,
                metadata: {
                  ...prev.metadata,
                  vprEnabled: enabled
                }
              }));
            }}
          />
        );

      default:
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800">Welcome</h2>
            <p className="text-gray-600 mt-2">Select a tab to get started building your guide.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(to right bottom, rgb(235 235 235), rgb(183 183 183))" }}>
      {/* Header */}
      <div className="bg-white/90 backdrop-blur shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img
                src={Logo_Circle}
                alt="Guide Sofia Logo"
                className="w-12 h-12 rounded-xl object-cover"
              />
              <h1 className="text-2xl font-bold text-gray-800">GuideSofia - Audioguide Creator Tool</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full text-sm font-semibold">
                {userRole || 'User'}
              </span>
      
            </div>
          </div>
        </div>
      </div>

      {/* SuperAdmin Permission Manager Button */}
      {userRole === 'superadmin' && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => uiState.setShowPermissionManager(!uiState.showPermissionManager)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-purple-700 flex items-center gap-2"
          >
            🛡️ Permission Manager
          </button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Step Progress */}
        {uiState.currentStep > 0 && (
          <div className="bg-white/90 backdrop-blur rounded-2xl p-3 mb-4 shadow-xl">
            <StepWizard
              currentStep={uiState.currentStep}
              onStepChange={services.itineraryActions.handleStepChange}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl overflow-auto">
          {/* Step 0: Welcome Screen */}
          {uiState.currentStep === 0 && (
            <div className="p-16 text-center">
              <div className="w-20 h-20 mx-auto mb-6 text-blue-500 flex items-center justify-center">
                <svg className="w-20 h-20" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Welcome to GuideSofia Backend
              </h2>
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                Create immersive audio-guided tours with rich multimedia content.
                Get started by importing existing guides or creating new ones.
              </p>

              <button
                onClick={() => uiState.setCurrentStep(1)}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl px-12 py-6 text-xl font-semibold hover:scale-105 transition-transform shadow-lg"
              >
                Get Started
              </button>
            </div>
          )}

          {/* Step 1: Import Guide */}
          {uiState.currentStep === 1 && (
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 text-blue-500 flex items-center justify-center">
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2V7z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Import Guide</h2>
                <p className="text-lg text-gray-600">Browse published guides or start fresh</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-6 justify-center mb-12">
                <button
                  onClick={services.itineraryActions.handleCreateNew}
                  className="group relative bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl p-8 min-w-[250px] hover:scale-105 transition-transform shadow-lg"
                >
                  <div className="w-12 h-12 mx-auto mb-4">
                    <svg fill="currentColor" viewBox="0 0 20 20" className="w-12 h-12">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Create New Guide</h3>
                  <p className="text-sm opacity-90">
                    Start fresh with a blank guide
                  </p>
                </button>

                <button
                  onClick={() => uiState.setShowDraftManager(true)}
                  className="group bg-white border-2 border-gray-300 text-gray-800 rounded-2xl p-8 min-w-[250px] hover:border-purple-500 hover:scale-105 transition-all shadow-lg"
                >
                  <div className="w-12 h-12 mx-auto mb-4 text-purple-500">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-12 h-12">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Manage Drafts</h3>
                  <p className="text-gray-600">
                    View and edit your saved drafts
                  </p>
                </button>

                <button
                  onClick={() => {
                    const fileInput = document.getElementById('file-upload-direct');
                    if (fileInput) {
                      fileInput.click();
                    }
                  }}
                  className="group bg-white border-2 border-gray-300 text-gray-800 rounded-2xl p-8 min-w-[250px] hover:border-blue-500 hover:scale-105 transition-all shadow-lg"
                >
                  <div className="w-12 h-12 mx-auto mb-4 text-blue-500">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-12 h-12">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Load from File</h3>
                  <p className="text-sm text-gray-600">
                    Upload a saved guide file
                  </p>
                </button>

                <label className="group bg-white border-2 border-gray-300 text-gray-800 rounded-2xl p-8 min-w-[250px] hover:border-purple-500 hover:scale-105 transition-all shadow-lg cursor-pointer">
                  <div className="w-12 h-12 mx-auto mb-4 text-purple-500">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-12 h-12">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Import from TXT</h3>
                  <p className="text-sm text-gray-600">
                    Upload <code className="bg-gray-100 px-1 rounded text-xs">01_Stop_Name.txt</code> files — one per stop
                  </p>
                  <input
                    type="file"
                    onChange={services.fileOperations.handleImportFromTxtFiles}
                    className="hidden"
                    accept=".txt"
                    multiple
                  />
                </label>
              </div>

              {/* Bulk SEO Operations */}
              <div className="mt-6 flex justify-end gap-2">
                <GenerateIndexOnlyButton />
                <GenerateAllSeoButton />
              </div>

              {/* Published Guides Listing */}
              <GuidesListing onLoadGuide={services.itineraryActions.handleLoadGuideFromListing} />

              {/* Published Venues Listing */}
              <div className="mt-6">
                <PublishedVenuesListing onLoadVenue={services.itineraryActions.handleLoadVenueFromListing} />
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={() => uiState.setCurrentStep(0)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
                >
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Guide Management (Content Creation) */}
          {uiState.currentStep === 2 && (
            <>
              {/* Guide Header */}
              <div className="bg-gray-50 p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {itineraryState.itinerary.metadata.name || 'Untitled Guide'}
                  </h2>

                </div>
              </div>

              {/* Tabs */}
              {!isVenueEditingContext && (
                <BuilderTabs
                  activeTab={uiState.activeTab}
                  onTabChange={handleSwitchTab}
                  showVenuePage={showVenuePageTab}
                  stopsCount={itineraryState.itinerary?.points?.length || 0}
                />
              )}

              {/* Tab Content */}
              <div className="p-6 overflow-auto">
                {renderTabContent()}
              </div>

              {/* Step 2 Navigation */}
              {isVenueEditingContext ? (
                <div className="flex justify-between p-6 bg-gray-50 border-t">
                  <button
                    onClick={() => uiState.setCurrentStep(1)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back to Published Venues
                  </button>
                </div>
              ) : (
                <div className="flex justify-between p-6 bg-gray-50 border-t">
                  <button
                    onClick={() => uiState.setCurrentStep(1)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back to Load/Create
                  </button>
                  <button
                    onClick={() => uiState.setCurrentStep(3)}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Continue to Review →
                  </button>
                </div>
              )}
            </>
          )}

          {/* Step 3: Review */}
          {uiState.currentStep === 3 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Review Your Guide
                  </h2>
                  <button
                    onClick={() => setShowGoogleDocsExport(true)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 text-sm"
                    title="Extract all guide text and export to Google Docs"
                  >
                    📄 Extract to Google Docs
                  </button>
                </div>

                <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr),minmax(0,1.3fr)]">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Guide Summary</h2>
                    <div className="space-y-3 text-sm">
                      <div>
                        <div className="text-gray-500">Guide name</div>
                        <div className="font-medium text-gray-800">{itineraryState.itinerary.metadata.name || 'Missing'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Guide ID</div>
                        <div className={`font-medium ${reviewSummary.hasValidGuideId ? 'text-green-700' : 'text-red-700'}`}>
                          {itineraryState.itinerary.metadata.id || 'Missing'}
                        </div>
                        <div className="text-xs text-gray-500">Expected format: 13 digits, for example 1780599465639.</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-gray-500">Language</div>
                          <div className="font-medium text-gray-800">{itineraryState.itinerary.metadata.language || 'Missing'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Stops</div>
                          <div className="font-medium text-gray-800">{narratedPoints.length}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Popup stops</div>
                          <div className="font-medium text-gray-800">{popupStopPoints.length}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Incomplete stops</div>
                          <div className={`font-medium ${reviewSummary.incompleteStops.length ? 'text-amber-700' : 'text-green-700'}`}>
                            {reviewSummary.incompleteStops.length}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Stops missing audio</div>
                          <div className={`font-medium ${reviewSummary.audioMissingStops.length ? 'text-red-700' : 'text-green-700'}`}>
                            {reviewSummary.audioMissingStops.length}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Audio language issues</div>
                          <div className={`font-medium ${reviewSummary.audioLanguageIssueStops.length ? 'text-red-700' : 'text-green-700'}`}>
                            {reviewSummary.audioLanguageIssueStops.length}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Stops missing images</div>
                          <div className={`font-medium ${reviewSummary.imageMissingStops.length ? 'text-red-700' : 'text-green-700'}`}>
                            {reviewSummary.imageMissingStops.length}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500">Saved routes file</div>
                          <div className={`font-medium ${
                            !reviewSummary.requiresSavedRoutes
                              ? 'text-gray-700'
                              : reviewSummary.savedRoutesFileStatus === 'present'
                              ? 'text-green-700'
                              : reviewSummary.savedRoutesFileStatus === 'checking'
                              ? 'text-amber-700'
                              : 'text-red-700'
                          }`}>
                            {!reviewSummary.requiresSavedRoutes
                              ? 'Not needed'
                              : reviewSummary.savedRoutesFileStatus === 'present'
                              ? 'Uploaded'
                              : reviewSummary.savedRoutesFileStatus === 'checking'
                              ? 'Checking...'
                              : 'Missing'}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Description</div>
                        <p className="text-gray-700">{itineraryState.itinerary.metadata.description || 'Missing'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Readiness Check</h2>

                    {reviewSummary.totalIssueCount === 0 ? (
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" />
                        <div>All required guide and stop fields are present.</div>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                          <div>
                            {reviewSummary.totalIssueCount} missing required field{reviewSummary.totalIssueCount === 1 ? '' : 's'} found across the guide.
                          </div>
                        </div>

                        {reviewSummary.metadataIssues.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Guide info missing</h3>
                            <ul className="space-y-2">
                              {reviewSummary.metadataIssues.map((issue) => (
                                <li key={issue} className="text-sm text-gray-700 flex items-start gap-2">
                                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                                  <span>{issue}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {reviewSummary.requiresSavedRoutes && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Saved routes file</h3>
                            {reviewSummary.savedRoutesFileStatus === 'present' ? (
                              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                                Saved routes file found{reviewSummary.savedRoutesFilePath ? ` at ${reviewSummary.savedRoutesFilePath}` : '.'}
                              </div>
                            ) : reviewSummary.savedRoutesFileStatus === 'checking' ? (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                Checking for the uploaded routes file...
                              </div>
                            ) : (
                              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                No uploaded saved routes file found{reviewSummary.savedRoutesFilePath ? ` at ${reviewSummary.savedRoutesFilePath}` : '.'}
                              </div>
                            )}
                          </div>
                        )}

                        {Object.keys(reviewSummary.stopFieldCounts).length > 0 && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Missing fields across stops</h3>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(reviewSummary.stopFieldCounts).map(([field, count]) => (
                                <span key={field} className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700">
                                  {field}: {count}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {reviewSummary.incompleteStops.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Stops that still need work</h3>
                            <div className="space-y-3 max-h-[24rem] overflow-auto pr-1">
                              {reviewSummary.incompleteStops.map((stop) => (
                                <div key={stop.id} className="rounded-lg border border-gray-200 p-3">
                                  <div className="font-medium text-gray-800">{stop.label}</div>
                                  <div className="text-sm text-gray-600">{stop.missingFields.join(', ')}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {reviewSummary.audioMissingStops.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Stops missing audio</h3>
                            <div className="space-y-3 max-h-[18rem] overflow-auto pr-1">
                              {reviewSummary.audioMissingStops.map((stop) => (
                                <div key={stop.id} className="rounded-lg border border-gray-200 p-3">
                                  <div className="font-medium text-gray-800">{stop.label}</div>
                                  <div className="text-sm text-gray-600">No audio file is attached.</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {reviewSummary.audioLanguageIssueStops.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Audio language issues</h3>
                            <div className="space-y-3 max-h-[18rem] overflow-auto pr-1">
                              {reviewSummary.audioLanguageIssueStops.map((stop) => (
                                <div key={stop.id} className="rounded-lg border border-gray-200 p-3">
                                  <div className="font-medium text-gray-800">{stop.label}</div>
                                  <div className="text-sm text-gray-600">{stop.detail}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {reviewSummary.imageMissingStops.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Stops missing images</h3>
                            <div className="space-y-3 max-h-[18rem] overflow-auto pr-1">
                              {reviewSummary.imageMissingStops.map((stop) => (
                                <div key={stop.id} className="rounded-lg border border-gray-200 p-3">
                                  <div className="font-medium text-gray-800">{stop.label}</div>
                                  <div className="text-sm text-gray-600">No stop image is attached.</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => uiState.setCurrentStep(2)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back to Build
                  </button>
                  <button
                    onClick={() => uiState.setCurrentStep(4)}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Continue to Export →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Export */}
          {uiState.currentStep === 4 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Export Your Guide
                  </h2>
                </div>

                <div className="space-y-6">
                  {/* Action Buttons Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-between items-center mb-6"> 
                      <SeoPageButton itinerary={itineraryState.itinerary} />
                    </div>
                    <ActionButtons
                      onSaveDraft={() => uiState.setShowDraftSaveDialog(true)}
                      onSaveComplete={services.fileOperations.handleSaveCompleteGuide}
                      onGenerateSQL={services.databaseOperations.generateSqlCommands}
                      onRunSQL={services.databaseOperations.executeSql}
                      onExport={() => services.fileOperations.saveItineraryToFile(itineraryState.itinerary)}
                      onLoadGuide={() => {
                        const fileInput = document.getElementById('file-upload-direct');
                        if (fileInput) {
                          fileInput.click();
                        }
                      }}
                      copied={uiState.copied}
                      executing={uiState.executing}
                    />
                  </div>

                  {/* Firebase Export Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <FirebaseExporterSection
                      itinerary={itineraryState.itinerary}
                      setItinerary={itineraryState.setItinerary}
                      onStatusChange={(status, message) => {
                        // Handle status changes from Firebase export
                        console.log('Firebase export status:', status, message);
                      }}
                    />
                  </div>

                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => uiState.setCurrentStep(3)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back to Review
                  </button>
        
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <SqlDialog
        isOpen={uiState.showSqlDialog}
        onClose={() => uiState.setShowSqlDialog(false)}
        sqlCommand={itineraryState.sqlCommand}
        onCopy={services.databaseOperations.copySqlToClipboard}
        copied={uiState.copied}
        onExecute={services.databaseOperations.executeSql}
        executing={uiState.executing}
        executionResult={uiState.executionResult}
      />

      <LoadGuideDialog
        isOpen={uiState.showLoadModal}
        onClose={() => uiState.setShowLoadModal(false)}
        onFileLoad={services.fileOperations.handleLoad}
        onLoadFromListing={services.itineraryActions.handleLoadGuideFromListing}
        onCreateNew={services.itineraryActions.handleCreateNew}
        onImportFromTxt={services.fileOperations.handleImportFromTxtFiles}
        isLoading={uiState.isLoading}
        loadProgress={uiState.loadProgress}
      />

      <DraftSaveDialog
        isOpen={uiState.showDraftSaveDialog}
        onClose={() => uiState.setShowDraftSaveDialog(false)}
        itinerary={itineraryState.itinerary}
        onSave={async (draftData) => {
          try {
            const { saveDraftToStorage } = await import('./ItineraryCreator/utils/draftUtils');
            const { createDraftRecord } = await import('./ItineraryCreator/utils/draftDatabase');

            // Ensure we save the current POI changes to the itinerary before saving draft
            const currentItinerary = { ...itineraryState.itinerary };

            // If there's a current POI being edited with an ID, make sure it's updated in the points array
            if (itineraryState.currentPoi && itineraryState.currentPoi.id) {
              const poiIndex = currentItinerary.points.findIndex(p => p.id === itineraryState.currentPoi.id);
              if (poiIndex !== -1) {
                currentItinerary.points[poiIndex] = { ...itineraryState.currentPoi };
              }
            }

            // Save to Firebase Storage
            const storageResult = await saveDraftToStorage(currentItinerary, currentItinerary.metadata);
            console.log('Draft saved to storage:', storageResult);

            // Create database record for the draft
            const draftRecord = {
              filename: storageResult.filename,
              country: currentItinerary.metadata.country || 'Unknown',
              language: currentItinerary.metadata.language || 'en',
              city: currentItinerary.metadata.city || '',
              title: currentItinerary.metadata.name || 'Untitled Guide',
              description: currentItinerary.metadata.description || '',
              createdBy: 'anonymous', // TODO: Replace with actual user ID when auth is implemented
              storageUrl: storageResult.storagePath, // This is the Firebase Storage path that loadDraftFromStorage expects
              pointsCount: currentItinerary.points?.length || 0,
              estimatedDuration: currentItinerary.metadata.duration || 0
            };

            const dbResult = await createDraftRecord(draftRecord);
            console.log('Draft record created in database:', dbResult);

            uiState.setShowDraftSaveDialog(false);

            // Show success message
            alert('Draft saved successfully and will appear in the draft manager!');
          } catch (error) {
            console.error('Failed to save draft:', error);
            alert(`Failed to save draft: ${error.message}`);
          }
        }}
      />

      {/* Draft Manager Dialog */}
      {uiState.showDraftManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto">
          <div className="min-h-screen p-4">
            <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-xl relative">
              <button
                onClick={() => uiState.setShowDraftManager(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <DraftManager
                currentDraftId={draftState.currentDraftId}
                onLoadDraft={(draft) => {
                  // Load the draft and close the manager
                  services.itineraryActions.handleLoadGuideFromListing(draft);
                  uiState.setShowDraftManager(false);
                }}
                onDeleteDraft={() => {
                  // Refresh the draft list after deletion
                }}
              />
            </div>
          </div>
        </div>
      )}

      {userRole === 'superadmin' && (
        <PermissionManagerDialog
          isOpen={uiState.showPermissionManager}
          onClose={() => uiState.setShowPermissionManager(false)}
        />
      )}

      <QADialog
        isOpen={uiState.showQADialog}
        onClose={() => {
          uiState.setShowQADialog(false);
          setQaResults(null);
          setQaType(null);
        }}
        qaResults={qaResults}
        qaType={qaType}
        isLoading={isQAChecking}
        onApplyChanges={handleApplyQAChanges}
      />

      {/* Google Docs Export Dialog */}
      {showGoogleDocsExport && (
        <GoogleDocsExportDialog
          itinerary={itineraryState.itinerary}
          onClose={() => setShowGoogleDocsExport(false)}
        />
      )}

      {/* Hidden file input for direct loading */}
      <input
        type="file"
        onChange={services.fileOperations.handleLoad}
        className="hidden"
        id="file-upload-direct"
        accept=".json"
      />

      {/* SQL Execution Result Toast */}
      {uiState.executionResult && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div className={`max-w-md rounded-lg shadow-lg p-4 border-l-4 ${
            uiState.executionResult.success
              ? 'bg-green-50 border-green-500'
              : 'bg-red-50 border-red-500'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className={`font-semibold ${
                  uiState.executionResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {uiState.executionResult.success ? '✓ Success' : '✗ Error'}
                </p>
                <p className={`text-sm mt-1 ${
                  uiState.executionResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {uiState.executionResult.message}
                </p>
                {uiState.executionResult.affectedRows !== undefined && (
                  <p className="text-sm text-green-600 mt-1">
                    Affected rows: {uiState.executionResult.affectedRows}
                  </p>
                )}
              </div>
              <button
                onClick={() => uiState.setExecutionResult(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuideBuilder;
